import React, {
  useState,
  useCallback,
  useRef,
  useMemo,
  useEffect,
} from "react";
import {
  Platform,
  View,
  Pressable,
  Text,
  Image,
  ScrollView,
  type LayoutChangeEvent,
  type ViewStyle,
} from "react-native";
import { useColorScheme } from "../foundations/colorScheme";
import {
  ArrowRight,
  BarChart3,
  Bookmark,
  CalendarClock,
  CalendarPlus,
  Camera,
  ChevronDown,
  Dices,
  FileText,
  Image as ImageIcon,
  type LucideIcon,
  Mic,
  MapPin,
  Pencil,
  Play,
  Plus,
  Send,
  Smile,
  Trash2,
  X,
} from "lucide-react-native";
import { createTheme } from "../design-system/theme";
import { formatMessagePreview, PreviewLabel } from "../appEvent";
import { log } from "../log/log";
import {
  RichTextEditor,
  type RichTextEditorHandle,
} from "./composer/RichTextEditor";
import {
  EnrichedRichTextEditor,
  type EnrichedActiveFormats,
  type EnrichedRichTextEditorHandle,
} from "./composer/EnrichedRichTextEditor";
import type {
  EnrichedCodeLanguageContext,
  EnrichedLinkEditContext,
  EnrichedTriggerContext,
} from "@ping/enriched-text-input";
import { decodeBubbleBody } from "./decodeBubbleBody";
import { LinkEditPopover } from "./composer/LinkEditPopover";
import { CodeLanguagePickerOverlay } from "./composer/CodeLanguagePickerOverlay";
import { fileToImageSource } from "./composer/fileToImageSource";
import {
  loadComposerDraft,
  saveComposerDraft,
} from "./composer/composerDraftStore";
import { WebPortal } from "./composer/WebPortal";
import type { RichTextEditorState } from "./composer/RichTextEditorState";
import type { RichTextFormat } from "./composer/RichTextFormat";
import { FormatOptions } from "./composer/FormatOptions";
import { FloatingFormatToolbar } from "./composer/FloatingFormatToolbar";
import { SlashCommandPicker } from "./composer/SlashCommandPicker";
import {
  BUILTIN_SLASH_COMMANDS,
  filterSlashCommands,
  matchSlashQuery,
  runSlashCommand,
  type SlashCommand,
} from "./composer/slashCommands";
import { AnchoredPopover } from "../components/AnchoredPopover";
import { ScheduleMessageModal } from "./composer/ScheduleMessageModal";
import { ScheduledMessagesBar } from "./composer/ScheduledMessagesBar";
import {
  cancelScheduledMessage,
  listScheduledMessages,
  registerScheduledSender,
  scheduleMessage,
  subscribeScheduled,
  type ScheduledMessage,
} from "./composer/scheduledMessages";
import {
  SCHEDULE_PRESETS,
  formatScheduleShort,
} from "./composer/schedulePresets";
import { CreateSavedMessageModal } from "./composer/CreateSavedMessageModal";
import {
  addSavedMessage,
  listSavedMessages,
  removeSavedMessage,
  subscribeSavedMessages,
  type SavedMessage,
} from "./composer/savedMessages";
import {
  applyFormatToggle,
  deriveActiveFormats,
  insertLink,
  toggleLinePrefix,
  toggleNumberedList,
} from "./composer/richTextActions";
import { useChatRoomShellHandlers } from "./ChatRoomShell";
import { styles } from "./ChatRoomComposer.styles";
import type { Message } from "../sync/persistence";
import { MentionPicker, useMentionPicker } from "./composer/MentionPicker";
import type { MentionCandidate } from "../chat/ChatRoom.types";
import {
  ComposerEmojiOverlay,
  type ComposerEmojiOverlayItem,
} from "./composer/ComposerEmojiOverlay";
import { EMOJI_CATALOG, filterEmojiCatalog } from "./composer/emojiCatalog";
import type {
  GifItem,
  GifSearchProvider,
  StickerSearchProvider,
} from "./composer/GifSearchProvider";
import { ComposerVoiceBar } from "./composer/ComposerVoiceBar";
import {
  Popover,
  PopoverAnchor,
  type PopoverAnchorHandle,
} from "./composer/Popover";

/**
 * Callback for live audio-amplitude streaming during voice recording.
 * The host wires this to its native recorder (AnalyserNode on web,
 * `currentMetering` from `react-native-audio-recorder-player` on
 * desktop). Returns an unsubscribe function the composer calls when
 * recording stops.
 */
export type ComposerAmplitudeSubscriber = (
  listener: (amplitude: number) => void,
) => () => void;

// ---------------------------------------------------------------------------
// Cycle 16 Wave 3 WEB-WIRE-B — additive rich-text composer.
// ---------------------------------------------------------------------------
//
// `ChatRoomComposerRich` swaps the inline `<TextInput />` for the Wave-1
// `<RichTextEditor />` and adds an attachment menu that routes Poll /
// Location / Image actions through the shell's overlay handlers. The
// legacy `ChatRoomComposer` export above is preserved so existing
// chat-surface call sites (`apps/web/src/App.tsx`) keep rendering until
// WEB-WIRE-A switches the root.
//
// Voice / Doc attach are forwarded to host-provided callbacks (the host
// already owns the `<input type="file">` element + the
// `VoiceMessageRecorder` overlay).

/**
 * Attach-menu entries. Order + labels match Figma `2804:26754`
 * (Action menu) — Camera / Photos / Files / Location / Poll / Event
 * / Games. `voice` is retained for hosts that wired the Mic-from-
 * menu path; the new design omits it from the visible list (the mic
 * lives inside the input pill instead).
 */
export type ChatRoomAttachMenuKind =
  | "camera"
  | "photo"
  | "doc"
  | "location"
  | "poll"
  | "event"
  | "games"
  | "voice";

export interface ChatRoomComposerRichProps {
  readonly onSend: (text: string, inReplyTo?: string) => Promise<void>;
  readonly replyTo?: Message | null;
  readonly onCancelReply?: () => void;
  /**
   * Edit mode. When set, the composer pre-fills with the message's text, shows
   * an "Editing message" chip, and Send calls `onSubmitEdit(newText)` instead of
   * `onSend`. Mirror of the reply-mode props. `onCancelEdit` clears the mode
   * (host sets `editingMessage` back to null).
   */
  readonly editingMessage?: Message | null;
  readonly onSubmitEdit?: (newText: string) => Promise<void>;
  readonly onCancelEdit?: () => void;
  /**
   * Optional display name of the replied-to message's author, rendered as
   * the bold header of the reply chip (Figma `2804:27420`). The composer
   * receives `replyTo` as a bare `Message` (no resolved sender), so the
   * host passes the human-readable name here when it has one. Undefined-safe:
   * falls back to a neutral label when omitted.
   */
  readonly replyToSenderName?: string;
  /**
   * Optional avatar for the replied-to author — `imageUri` for a photo,
   * `initials` for the fallback chip. Both optional; the chip shows an
   * empty rounded placeholder when neither is supplied.
   */
  readonly replyToAvatar?: {
    readonly imageUri?: string | undefined;
    readonly initials?: string | undefined;
  };
  /** Resolved thumbnail of the replied-to message when it is media
   *  (photo/video/GIF) — shows a real square in the pill (iOS parity). */
  readonly replyToThumbnailUri?: string | undefined;
  readonly replyToThumbnailKind?: 'image' | 'video' | 'gif' | undefined;
  readonly disabled?: boolean;
  /**
   * Optional host hook that picks photo / camera sources. When invoked,
   * the host opens its picker and resolves with the picked
   * `ImageSource[]`; the composer forwards the array to the shell's
   * `openImageEditor` handler so the editor lands in the modal stack.
   */
  readonly onPickImages?: () => Promise<void>;
  /** Optional doc-attach handler. The host owns the file-input element. */
  readonly onPickDoc?: () => Promise<void>;
  /** Optional camera capture ("+" → Camera). Falls back to image pick. */
  readonly onCaptureCamera?: () => Promise<void> | void;
  /**
   * True when the host has staged attachments in the tray — keeps the
   * Send button enabled (and submit firing) even with an empty text
   * box, so the user can send attachments alone.
   */
  readonly hasStagedAttachments?: boolean;
  /**
   * Optional voice-record handler. Called when the user taps the mic
   * (entering the recording UI state). The host owns the underlying
   * MediaRecorder; the composer purely drives the state machine.
   */
  readonly onStartVoice?: () => void;
  /**
   * Optional voice-send handler. Called when the user taps Send while
   * recording. The host stops its recorder, builds the `com.ping.voice`
   * envelope, and routes through MLS. `durationMs` is the elapsed
   * recording time as observed by the composer's UI ticker; the host
   * may overwrite it with the recorder's authoritative duration when
   * available.
   */
  readonly onSendVoiceMessage?: (durationMs: number) => Promise<void>;
  /**
   * Optional voice-cancel handler. Called when the user dismisses the
   * recording state. Hosts should call `cancel()` on their recorder
   * to release the microphone and discard any captured audio.
   */
  readonly onCancelVoiceMessage?: () => void;
  /**
   * Optional mention directory. When supplied, typing `@` in the
   * composer opens the `<MentionPicker />` popover above the input pill;
   * selection inserts `@<displayName> ` at the caret. Visual reference:
   * Figma `2918:10419` (Mention section) — the candidate row mirrors
   * `tag-you` (`2918:10420`) and the inline pill mirrors
   * `tagged-own/other` (`2918:10446/10443`).
   */
  readonly mentionCandidates?: ReadonlyArray<MentionCandidate> | undefined;
  /**
   * Optional GIF search adapter. When supplied, the unified
   * Emoji/GIF popover (Figma `2804:26810` / `2804:27348`) renders
   * its GIFs tab as interactive — `trending()` is called on first
   * open and on every query clear, `search(q)` runs on non-empty
   * input. When omitted the GIFs tab is disabled (40% opacity)
   * and the popover stays on the Emoji tab.
   *
   * The Figma source intentionally has NO separate GIF trigger
   * button — both Emoji and GIF panels share the same tabbed
   * popover, opened by the Smile affordance inside the input
   * pill (`2804:26707`). See
   * `tmp/figma-chatroom-redo/emoji-gif-extracted.md`.
   */
  readonly gifProvider?: GifSearchProvider | undefined;
  /**
   * Optional GIF-send handler. Called when the user taps a
   * thumbnail in the GIFs tab. The host encodes the selection
   * into a `com.ping.gif` envelope and sends through MLS.
   */
  readonly onSendGif?: ((gif: GifItem) => Promise<void>) | undefined;
  /**
   * Optional sticker search adapter, mirroring `gifProvider`. When
   * supplied the Stickers tab in the unified popover is interactive
   * (trending on first open / query-driven search) and selections
   * route through `onSendSticker`. When omitted the tab is dimmed.
   */
  /**
   * Mirrors the user-facing "Press Enter to send" setting. When
   * `true` (default), Enter submits the message and Shift+Enter
   * inserts a newline. When `false`, Enter inserts a newline and
   * the only send path is the send button. Hosts read this from
   * their user-settings store and forward it here.
   */
  readonly pressEnterToSend?: boolean | undefined;
  readonly stickerProvider?: StickerSearchProvider | undefined;
  /**
   * Optional sticker-send handler. Mirrors `onSendGif` — the host
   * encodes the selection into a `com.ping.gif` envelope (stickers
   * ride the same envelope kind on the wire; the source URL marks
   * them as sticker-typed media).
   */
  readonly onSendSticker?: ((sticker: GifItem) => Promise<void>) | undefined;
  /**
   * Optional live-amplitude stream. When supplied, the recording
   * waveform animates in real time from the host's recorder. The
   * composer subscribes on `onStartVoice` and unsubscribes when the
   * recording ends. Listeners are invoked with normalized 0..1
   * amplitude values; the composer keeps a rolling window for the
   * waveform bars (Figma `3125:3929`).
   */
  readonly subscribeAmplitudes?: ComposerAmplitudeSubscriber | undefined;
  /**
   * Optional handler invoked when the user pastes (or drag-drops) an
   * image into the web composer. Hosts wire this to their attachment
   * upload pipeline — the file is routed as an attachment, NOT
   * inlined into the message body.
   */
  readonly onPasteImage?: ((file: File) => void) | undefined;
  /**
   * Fired on every composer text change. Hosts use this for ephemeral
   * typing indicators (`/v1/ephemeral`).
   */
  readonly onComposerTextChange?: ((text: string) => void) | undefined;
  /**
   * Optional "edit your last message" handler (Slack ↑-in-empty-composer).
   * When supplied, pressing ↑ while the composer is empty asks the host to
   * load the user's most recent sent message into an edit composer. The
   * host owns the edit surface + which message qualifies. Web only (the
   * contenteditable surfaces the key); omit to disable.
   */
  readonly onEditLastMessage?: (() => void) | undefined;
  /**
   * Stable per-conversation key (the conversation id). When set, the
   * composer caches the in-progress message under this key so reloading
   * the browser or switching conversations never loses typing progress;
   * the cache is cleared the moment the composer is emptied or the
   * message is sent. The host keys the composer on the conversation id
   * (remount per conversation), so the draft loads on mount.
   */
  readonly draftKey?: string | undefined;
  // ── Feature-restriction flags. All default ON; set false to hide the
  // affordance in surfaces that should only allow a subset (e.g. the
  // attachment-caption box wants text + emoji + mentions only).
  /** Show the "+" attachment menu (camera/photos/files/poll/games/location). */
  readonly showAttachmentMenu?: boolean | undefined;
  /** Show the voice-record mic button. */
  readonly showVoiceRecording?: boolean | undefined;
  /** Enable the GIFs tab in the emoji popover (also needs `gifProvider`). */
  readonly showGifs?: boolean | undefined;
  /**
   * Enable the `/`-slash-command autocomplete (web). Defaults to `true`.
   * Set `false` in surfaces that shouldn't treat a leading slash as a
   * command (e.g. the attachment-caption box).
   */
  readonly showSlashCommands?: boolean | undefined;
  /**
   * Extra slash commands appended to the built-in text set (`/shrug`,
   * `/tableflip`, `/unflip`, `/lenny`). Hosts wire their own commands
   * here — each command's `run(args)` returns the rewritten message body
   * (or `null` to send nothing). Names collide-last: a host command with
   * the same name as a builtin overrides it.
   */
  readonly slashCommands?: ReadonlyArray<SlashCommand> | undefined;
  /**
   * Enable the "Schedule message" affordance on the send button (web).
   * Defaults to `true`. Needs either a `draftKey` (conversation id, for
   * the built-in client-side scheduler) or an `onScheduleMessage`
   * override. Set `false` to hide the chevron entirely.
   */
  readonly showSchedule?: boolean | undefined;
  /**
   * Enable the "Saved Messages" affordance (web/desktop). Defaults to
   * `true`. When on, the trailing send button's empty-composer state is
   * replaced by a bookmark button that opens a client-side saved-messages
   * popover; picking one fills the composer. Set `false` to keep the plain
   * (disabled) send button in surfaces that shouldn't offer canned replies.
   */
  readonly showSavedMessages?: boolean | undefined;
  /**
   * Host override for scheduled delivery. When provided, the composer
   * hands the (already slash-transformed) message body, an ISO-8601
   * `send_at`, and the reply target to the host instead of using the
   * built-in localStorage scheduler — e.g. to post to a backend
   * "send at" endpoint. The host owns persistence + delivery + surfacing
   * the pending item.
   */
  readonly onScheduleMessage?:
    | ((text: string, sendAtISO: string, replyTo?: string) => Promise<void>)
    | undefined;
  /**
   * Host-provided pending scheduled messages, surfaced in the bar above the
   * composer. Only used when `onScheduleMessage` is wired (host-driven
   * delivery) — the built-in client scheduler owns this list otherwise. Pair
   * with `onCancelScheduled` so the items stay cancelable.
   */
  readonly scheduledMessages?: readonly ScheduledMessage[] | undefined;
  /**
   * Cancel a host-scheduled message by id. Called when the user taps the ✕ on
   * a `scheduledMessages` row. Ignored unless `onScheduleMessage` is wired.
   */
  readonly onCancelScheduled?: ((id: string) => void) | undefined;
  /** Enable the Stickers tab in the emoji popover (also needs `stickerProvider`). */
  readonly showStickers?: boolean | undefined;
  /**
   * Whitelist which "+" menu entries appear. When omitted, all wired
   * entries show. Use to hide e.g. games/poll/location individually
   * while keeping photos/files.
   */
  readonly attachMenuKinds?: ReadonlyArray<ChatRoomAttachMenuKind> | undefined;
  /**
   * "Editor-only" mode. Renders the rich input pill (with its format
   * toolbar-on-selection + emoji picker) but NO trailing send / mic /
   * schedule / bookmark affordance and no Enter-to-send — turning the
   * composer into a reusable rich-text FIELD. Used by the "New Saved
   * Message" modal so its body gets the exact same formatting + emoji as
   * the chat composer. Pair with `onComposerTextChange` to read the markdown
   * and an `initialText` seed.
   */
  readonly editorOnly?: boolean | undefined;
  /** Override the input placeholder (defaults to "Type a message"). */
  readonly placeholder?: string | undefined;
  /**
   * One-shot initial buffer (markdown). Seeds the editor on mount — used by
   * `editorOnly` surfaces that aren't draft-backed. Ignored when a `draftKey`
   * draft exists (the draft wins).
   */
  readonly initialText?: string | undefined;
  /** Extra classes appended to the composer's root container. */
  readonly containerClassName?: string | undefined;
  /** Extra classes appended to the composer's input pill. */
  readonly composerInputClassName?: string | undefined;
  /** Composer's light background color. */
  readonly composerLightBackgroundColor?: string | undefined;
  /** Composer's dark background color. */
  readonly composerDarkBackgroundColor?: string | undefined;
  /**
   * Extra classes appended to the composer's inner ROW (the flex row that
   * carries the input pill + trailing affordance, with the default surface
   * background + padding). Lets an embedding surface (e.g. the saved-message
   * modal) neutralize the row's chrome — `px-0! py-0! bg-transparent!` — so the
   * pill blends into the host field. Wrapper-only; the pill's own styling is
   * untouched.
   */
  readonly containerContentClassName?: string | undefined;
  /**
   * Tighten the composer row's horizontal padding from `px-xl` (24) to
   * `px-sm` (8). Used by the narrow thread / call-thread panels so the
   * input pill isn't crowded by the wider chat-room inset.
   */
  readonly denseComposerPadding?: boolean | undefined;
  /**
   * Optional overrides for the poll / location / games attach entries.
   * When provided, these take precedence over the shell-handler context
   * (used by threads to retarget the overlays at the thread, not the
   * parent conversation).
   */
  readonly onOpenPoll?: (() => void) | undefined;
  /** Opens the meeting composer (Event attach entry). Overrides the shell
   *  handler so a thread composer could target its own conversation. */
  readonly onOpenEvent?: (() => void) | undefined;
  readonly onOpenLocation?: (() => void) | undefined;
  readonly onOpenGames?: (() => void) | undefined;
  readonly testID?: string;
}

const RICH_TEXT_PLACEHOLDER = "Type a message";

// Minimum characters typed after `:` before the inline emoji
// autocomplete opens. Keeps short emoticons (`:D`, `:P` + space) in the
// emoticon→emoji auto-convert path instead of racing the picker. See
// `handleWebEmojiTrigger`.
const EMOJI_TRIGGER_MIN_QUERY = 2;

// Web composer growth cap. The contenteditable auto-grows with content;
// past this height (~9 lines) it scrolls internally instead of pushing
// the message list up — matching Slack and the reference composer.
// `overflow: 'auto'` is web-only (applied under `isWeb`); RN-Web renders
// it straight to CSS. The vertical padding gives the text consistent
// breathing room in BOTH single- and multi-line states, since the input
// pill itself carries no y-padding (it relies on `min-h-10` centering
// for one line, which leaves multi-line text flush to the pill edges).
const WEB_EDITOR_MAX_HEIGHT = 208;
const WEB_EDITOR_SCROLL_STYLE = {
  maxHeight: WEB_EDITOR_MAX_HEIGHT,
  overflow: "auto",
  paddingTop: 6,
  paddingBottom: 6,
} as unknown as ViewStyle;

// Stable empty set for the web path, which never consumes buffer-derived active
// formats — avoids allocating a new Set (and re-parsing markdown) per keystroke.
const EMPTY_ACTIVE_FORMATS: ReadonlySet<RichTextFormat> =
  new Set<RichTextFormat>();

export const ChatRoomComposerRich: React.FC<ChatRoomComposerRichProps> = ({
  onSend,
  replyTo,
  onCancelReply,
  editingMessage,
  onSubmitEdit,
  onCancelEdit,
  replyToSenderName,
  replyToAvatar,
  replyToThumbnailUri,
  replyToThumbnailKind,
  disabled = false,
  onPickImages,
  onPickDoc,
  onCaptureCamera,
  hasStagedAttachments = false,
  onStartVoice,
  onSendVoiceMessage,
  onCancelVoiceMessage,
  mentionCandidates,
  gifProvider,
  onSendGif,
  stickerProvider,
  onSendSticker,
  subscribeAmplitudes,
  onComposerTextChange,
  onEditLastMessage,
  draftKey,
  pressEnterToSend = true,
  onPasteImage,
  showAttachmentMenu = true,
  showVoiceRecording = true,
  showGifs = true,
  showStickers = true,
  showSlashCommands = true,
  slashCommands,
  showSchedule = true,
  showSavedMessages = true,
  onScheduleMessage,
  scheduledMessages,
  onCancelScheduled,
  attachMenuKinds,
  containerClassName,
  composerInputClassName = "",
  containerContentClassName = "",
  composerLightBackgroundColor,
  composerDarkBackgroundColor,
  denseComposerPadding = false,
  onOpenPoll,
  onOpenEvent,
  onOpenLocation,
  onOpenGames,
  editorOnly = false,
  initialText,
  placeholder,
  testID,
}) => {
  const scheme = useColorScheme();
  const colorScheme = scheme === "dark" ? "dark" : "light";
  // Memoize the theme so it isn't rebuilt on every keystroke (each render
  // otherwise allocates a fresh theme object + downstream style objects).
  const theme = useMemo(() => createTheme(colorScheme), [colorScheme]);
  const shellHandlers = useChatRoomShellHandlers();

  // Seed from the cached draft on mount (the host remounts the composer
  // per conversation, so this runs once per conversation). Empty when no
  // draft / no `draftKey`.
  const [text, setText] = useState<string>(() => {
    const draft = draftKey !== undefined ? loadComposerDraft(draftKey) : "";
    // A saved draft wins; otherwise seed from `initialText` (editor-only
    // surfaces like the saved-message modal that aren't draft-backed).
    return draft.length > 0 ? draft : (initialText ?? "");
  });
  // EDIT MODE: pre-fill the composer with the target message's PLAIN text when
  // an edit starts (the `text` state is a one-shot initializer, so a dedicated
  // effect is required). `body` may be a full envelope (mentions/reply/link), so
  // decode it to the display string. Keyed by the editing target's id so it
  // seeds once per edit, not on every keystroke.
  const editingIdRef = useRef<string | null>(null);
  useEffect(() => {
    const id = editingMessage?.id ?? null;
    if (id === editingIdRef.current) return;
    editingIdRef.current = id;
    if (editingMessage != null) {
      setText(decodeBubbleBody(editingMessage.body));
    }
  }, [editingMessage]);
  // Persist the in-progress message. An empty buffer (deleted-all or post-send
  // reset) clears the cached draft IMMEDIATELY so a just-sent draft never
  // lingers; a non-empty buffer is DEBOUNCED (~300ms) so fast typing doesn't hit
  // synchronous localStorage on every keystroke. Send failures restore `text`,
  // which re-saves via this effect.
  const latestDraftTextRef = useRef(text);
  latestDraftTextRef.current = text;
  useEffect(() => {
    if (draftKey === undefined) return;
    if (text.length === 0) {
      saveComposerDraft(draftKey, text);
      return;
    }
    const id = setTimeout(() => saveComposerDraft(draftKey, text), 300);
    return () => clearTimeout(id);
  }, [draftKey, text]);
  // Flush the latest draft on unmount (conversation switch remounts the
  // composer via `key={conversationId}`), so the debounced tail is never lost.
  useEffect(() => {
    return () => {
      if (draftKey !== undefined && latestDraftTextRef.current.length > 0) {
        saveComposerDraft(draftKey, latestDraftTextRef.current);
      }
    };
  }, [draftKey]);
  const [editorState, setEditorState] =
    useState<RichTextEditorState>("collapsed");
  // Selection range tracked at the host so toolbar buttons +
  // keyboard shortcuts share a single source of truth with the
  // editor. `selStart`/`selEnd` are kept in sync via
  // `onSelectionRangeChange` from the RichTextEditor.
  const [selStart, setSelStart] = useState(0);
  const [selEnd, setSelEnd] = useState(0);
  // Drive the textarea's selection imperatively after a toolbar /
  // shortcut action that rewrites the buffer — RN's `<TextInput
  // selection>` prop is read on next layout, so storing this here
  // and feeding it through (see `pendingSelection` below) lets the
  // caret land where the action says it should.
  const [pendingSelection, setPendingSelection] = useState<{
    start: number;
    end: number;
  } | null>(null);
  // Active formats. Two code paths:
  //   - Web (Platform.OS === 'web'): the WYSIWYG `EnrichedRichTextEditor`
  //     reports its caret-driven active-format state via a callback;
  //     we store it in `webActive` and feed it to the toolbar. The
  //     contenteditable tracks bullet/numbered/link natively in
  //     addition to the contract-pinned `RichTextFormat` set.
  //   - Other platforms: derive from the markdown buffer + the host-
  //     tracked selection range, same as before.
  const [webActive, setWebActive] = useState<EnrichedActiveFormats>(() => ({
    formats: new Set(),
    bulletList: false,
    numberedList: false,
    link: false,
    codeLanguage: null,
    selectionEmpty: true,
  }));
  const isWeb = Platform.OS === "web";
  // On react-native-web, Pressable doesn't preventDefault on `mousedown`, so
  // clicking a trailing-slot button blurs the contenteditable editor BEFORE
  // the press fires. For the Saved-Messages bookmark that's fatal: the blur
  // flips `isInputFocused` false, the trailing affordance swaps to the idle
  // Mic, and the bookmark unmounts before its `onPress` runs (button vanishes,
  // popover never opens). Trapping mousedown keeps editor focus so the button
  // stays mounted and the popover opens — same guard `FormatOptions` uses.
  const webFocusGuard = isWeb
    ? ({
        onMouseDown: (e: { preventDefault: () => void }) => e.preventDefault(),
      } as unknown as Record<string, unknown>)
    : {};
  // On web the active formats come from the contenteditable engine (`webActive`),
  // so the buffer-derived set is DISCARDED — skip the full markdown re-parse it
  // does on every keystroke. Only the native path consumes this.
  const bufferActiveFormats = useMemo<ReadonlySet<RichTextFormat>>(
    () =>
      isWeb
        ? EMPTY_ACTIVE_FORMATS
        : deriveActiveFormats(text, selStart, selEnd),
    [isWeb, text, selStart, selEnd],
  );
  const activeFormats = isWeb ? webActive.formats : bufferActiveFormats;
  const [isSending, setIsSending] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  // Schedule-message affordance state (web). `scheduleMenuOpen` drives
  // the upward AnchoredPopover on the send button; `scheduleModalOpen`
  // drives the full "Schedule message" modal (custom time).
  const [scheduleMenuOpen, setScheduleMenuOpen] = useState(false);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  // Anchor for the schedule popover — the send-button wrapper's DOM node.
  const sendAreaRef = useRef<View | null>(null);
  // Saved-messages affordance state (web/desktop). `savedMenuOpen` drives the
  // upward popover anchored to the bookmark button; `savedModalOpen` drives
  // the "New Saved Message" modal. The list mirrors the client-side store and
  // stays in sync via `subscribeSavedMessages`.
  const [savedMenuOpen, setSavedMenuOpen] = useState(false);
  const [savedModalOpen, setSavedModalOpen] = useState(false);
  const [savedList, setSavedList] = useState<SavedMessage[]>(() =>
    listSavedMessages(),
  );
  useEffect(() => {
    setSavedList(listSavedMessages());
    return subscribeSavedMessages(() => setSavedList(listSavedMessages()));
  }, []);
  // Bookmark-button wrapper node — the saved-messages popover anchor.
  const savedAreaRef = useRef<View | null>(null);
  // Measured height of the composer row (the View that hosts the
  // attach button, input pill, and send/mic). The format toolbar
  // floats above this row, so its `bottom` offset has to track the
  // row's measured height as the textarea grows multi-line —
  // otherwise the chip stays pinned at a fixed offset and slips
  // behind the input pill on the second wrap. Initialized to 64 px
  // so the toolbar lands in the right place on first paint, before
  // the first `onLayout` fires (composer row is `pt-sm pb-lg` (24)
  // + the 40-px `min-h-10` input pill = 64).
  const [composerRowHeight, setComposerRowHeight] = useState(64);
  const handleComposerRowLayout = useCallback(
    (e: LayoutChangeEvent) => {
      const next = e.nativeEvent.layout.height;
      // Guard against zero / NaN; `Math.round` so trailing fractional
      // pixels from devicePixelRatio rounding don't trigger a re-render
      // every single keystroke.
      const rounded = Math.round(next);
      if (rounded > 0 && rounded !== composerRowHeight) {
        setComposerRowHeight(rounded);
      }
    },
    [composerRowHeight],
  );
  // Caret position is tracked on the host so the mention parser can walk
  // backwards from it without owning a ref to the inner TextInput. Updated
  // by the basic-input path below; rich-editor consumers can keep the
  // picker disabled by omitting `mentionCandidates`.
  const [caret, setCaret] = useState(0);
  const mentions = useMentionPicker(text, caret, mentionCandidates ?? []);

  // Web-only mention/emoji trigger state. The contenteditable editor
  // can't translate its caret into a string offset cheaply, so the
  // legacy `useMentionPicker` (which scans the markdown buffer) is
  // bypassed on web. Instead the editor surfaces trigger windows via
  // `onMentionTrigger` / `onEmojiTrigger` and we drive lightweight
  // local state off those. Mention selections commit through the
  // editor's `insertMention` imperative method; emoji shortcodes use
  // `insertEmoji`.
  const [webMentionTrigger, setWebMentionTrigger] =
    useState<EnrichedTriggerContext | null>(null);
  const webMentionQuery = webMentionTrigger?.query ?? null;
  // Highlighted row in the web mention popover, driven by ↑/↓. Reset to
  // 0 whenever the trigger window opens or the query changes (see the
  // effect below) so the first candidate is always pre-selected.
  const [webMentionIndex, setWebMentionIndex] = useState(0);
  // Last non-null caret anchor rect. `detectTrigger` occasionally returns
  // a null/zero rect for a collapsed range (some browsers, or `@` typed
  // as the first character of an empty editor); reusing the previous good
  // rect keeps the popover anchored instead of silently disappearing.
  const lastMentionAnchorRef =
    useRef<EnrichedTriggerContext["anchorRect"]>(null);
  const [webEmojiQuery, setWebEmojiQuery] = useState<string | null>(null);
  const handleWebMentionTrigger = useCallback(
    (ctx: EnrichedTriggerContext | null) => {
      if (ctx !== null && ctx.anchorRect !== null) {
        lastMentionAnchorRef.current = ctx.anchorRect;
      }
      setWebMentionTrigger(ctx);
    },
    [],
  );
  const handleWebEmojiTrigger = useCallback(
    (ctx: EnrichedTriggerContext | null) => {
      // Emoji `:` autocomplete only opens once the user has typed at
      // least `EMOJI_TRIGGER_MIN_QUERY` characters after the colon.
      // Below that we report the window closed so short emoticons —
      // `:D`, `:P`, `:o` + space — fall through to the editor's
      // emoticon→emoji auto-convert instead of the two fighting over
      // the same keystrokes. Mirrors the lexical composer's ≥3-char
      // `EmojiAutocompletePlugin` gate (which yields short faces to
      // its `EmoticonShortcodePlugin`).
      if (ctx === null || ctx.query.length < EMOJI_TRIGGER_MIN_QUERY) {
        setWebEmojiQuery(null);
        return;
      }
      setWebEmojiQuery(ctx.query);
    },
    [],
  );

  // Link-edit popover (Slack-style). When the user clicks an `<a>`
  // inside the contenteditable editor, the editor fires
  // `onLinkEdit` with the URL + caret anchor rect. We park the
  // popover there; Save → editor.setLink(newUrl), Remove →
  // editor.unwrapLink(). Both routes preserve the caret inside the
  // clicked link so the imperative methods know which anchor to
  // operate on.
  const [linkEdit, setLinkEdit] = useState<EnrichedLinkEditContext | null>(
    null,
  );
  const handleLinkEdit = useCallback((ctx: EnrichedLinkEditContext) => {
    setLinkEdit(ctx);
  }, []);
  // Code-block language picker (opened from a code block's language chip).
  const [codeLangCtx, setCodeLangCtx] =
    useState<EnrichedCodeLanguageContext | null>(null);
  const handleCodeLanguageOpen = useCallback(
    (ctx: EnrichedCodeLanguageContext) => setCodeLangCtx(ctx),
    [],
  );
  const handleLinkEditSave = useCallback(
    (nextUrl: string, nextLabel: string) => {
      const trimmed = nextUrl.trim();
      if (trimmed.length === 0) {
        webEditorRef.current?.unwrapLink();
      } else {
        const label = nextLabel.trim();
        webEditorRef.current?.setLink(
          trimmed,
          label.length > 0 ? label : undefined,
        );
      }
      setLinkEdit(null);
    },
    [],
  );
  const handleLinkEditRemove = useCallback(() => {
    webEditorRef.current?.unwrapLink();
    setLinkEdit(null);
  }, []);
  const handleLinkEditDismiss = useCallback(() => setLinkEdit(null), []);

  // Auto-dismiss the link popover when the caret moves away from
  // the link (the editor stops reporting `link` as an active format).
  // Without this the popover would linger after the user clicked
  // somewhere else in the editor.
  useEffect(() => {
    if (linkEdit !== null && !webActive.link) {
      setLinkEdit(null);
    }
  }, [webActive.link, linkEdit]);

  // Image-paste handler. The editor calls this with a `File`
  // pulled off the clipboard / drag-drop. We:
  //   1. Forward to the host's `onPasteImage` if wired (so the host
  //      can upload to its own attachment pipeline if it has one).
  //   2. Otherwise resolve the file's dimensions and open the
  //      shared image-editor overlay — same UI as picking from
  //      disk. Decoupling means image paste works in any host that
  //      mounted the shell, without forcing every host to wire its
  //      own callback.
  const handleEditorImagePaste = useCallback(
    (file: File) => {
      log.debug("composer.paste.image", {
        name: file.name,
        size: file.size,
        type: file.type,
      });
      if (onPasteImage !== undefined) {
        onPasteImage(file);
        return;
      }
      // Web hosts must wire `onPasteImage` for direct upload (no editor).
      if (Platform.OS === "web") {
        log.warn("composer.paste.image.unwired.web", {});
        return;
      }
      if (shellHandlers === null || shellHandlers === undefined) return;
      void (async () => {
        try {
          const source = await fileToImageSource(file);
          shellHandlers.openImageEditor([source]);
        } catch (err) {
          log.warn("composer.paste.image.failed", {
            err: (err as Error).message,
          });
        }
      })();
    },
    [onPasteImage, shellHandlers],
  );

  // Mention candidates filtered against the web trigger query.
  // Mirrors `useMentionPicker.filteredCandidates` so the popover can
  // show the same row shape on both code paths.
  const webMentionCandidates = useMemo(() => {
    if (webMentionQuery === null || mentionCandidates === undefined) return [];
    const q = webMentionQuery.toLowerCase();
    if (q.length === 0) return mentionCandidates.slice(0, 8);
    return mentionCandidates
      .filter((c) => c.displayName.toLowerCase().includes(q))
      .slice(0, 8);
  }, [webMentionQuery, mentionCandidates]);

  // Commit the candidate at the given index through the editor's
  // imperative `insertMention`, then close the popover. Shared by both
  // the keyboard (Enter) and mouse (click) selection paths so they stay
  // in lockstep.
  const commitWebMention = useCallback(
    (index: number) => {
      const candidate = webMentionCandidates[index];
      if (candidate === undefined) return;
      webEditorRef.current?.insertMention(
        candidate.displayName,
        candidate.userId,
      );
      setWebMentionTrigger(null);
      setWebMentionIndex(0);
    },
    [webMentionCandidates],
  );

  // Reset the highlight to the first row whenever the candidate set
  // changes (new query, popover just opened), and keep the index in
  // range if the filtered list shrank.
  useEffect(() => {
    setWebMentionIndex((prev) =>
      prev >= webMentionCandidates.length ? 0 : prev,
    );
  }, [webMentionCandidates.length, webMentionQuery]);

  // Keyboard navigation for the web mention popover. The contenteditable
  // editor's own keydown handler only knows about Escape, so ↑/↓/Enter
  // would otherwise move the caret / SEND the message. A capture-phase
  // listener on the document runs BEFORE React's root-level handlers, so
  // stopping propagation here suppresses the editor's send-on-Enter and
  // the browser's caret movement while the popover is open.
  const mentionOpen =
    isWeb && webMentionTrigger !== null && webMentionCandidates.length > 0;
  useEffect(() => {
    if (!mentionOpen) return;
    if (typeof document === "undefined") return;
    const onKeyDownCapture = (e: KeyboardEvent): void => {
      const len = webMentionCandidates.length;
      if (len === 0) return;
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          e.stopPropagation();
          setWebMentionIndex((i) => (i + 1) % len);
          break;
        case "ArrowUp":
          e.preventDefault();
          e.stopPropagation();
          setWebMentionIndex((i) => (i - 1 + len) % len);
          break;
        case "Enter":
        case "Tab":
          e.preventDefault();
          e.stopPropagation();
          setWebMentionIndex((i) => {
            commitWebMention(i);
            return 0;
          });
          break;
        case "Escape":
          e.preventDefault();
          e.stopPropagation();
          setWebMentionTrigger(null);
          break;
        default:
          break;
      }
    };
    document.addEventListener("keydown", onKeyDownCapture, true);
    return () =>
      document.removeEventListener("keydown", onKeyDownCapture, true);
  }, [mentionOpen, webMentionCandidates.length, commitWebMention]);

  // ── Slash commands ────────────────────────────────────────────────
  // `/name` typed at the START of the composer opens a command picker;
  // the command rewrites the outgoing message at send time (see
  // `handleSend`). Built-in commands are the host-independent `/shrug`
  // family; hosts append more via the `slashCommands` prop (last wins on
  // a name clash). Web only — the picker's keyboard nav needs the
  // document-level capture listener below.
  const slashCommandsAll = useMemo<readonly SlashCommand[]>(() => {
    if (slashCommands === undefined || slashCommands.length === 0) {
      return BUILTIN_SLASH_COMMANDS;
    }
    const byName = new Map<string, SlashCommand>();
    for (const c of BUILTIN_SLASH_COMMANDS) byName.set(c.name, c);
    for (const c of slashCommands) byName.set(c.name, c);
    return Array.from(byName.values());
  }, [slashCommands]);
  const slashQuery =
    isWeb && showSlashCommands && editingMessage == null
      ? matchSlashQuery(text)
      : null;
  const filteredSlashCommands = useMemo(
    () =>
      slashQuery !== null
        ? filterSlashCommands(slashCommandsAll, slashQuery)
        : [],
    [slashQuery, slashCommandsAll],
  );
  const [slashIndex, setSlashIndex] = useState(0);
  // Escape dismisses the picker for the CURRENT query only; typing more
  // re-opens it. Mirrors the reference's `dismissedForQueryRef`.
  const [slashDismissedQuery, setSlashDismissedQuery] = useState<string | null>(
    null,
  );
  const slashOpen =
    isWeb &&
    slashQuery !== null &&
    filteredSlashCommands.length > 0 &&
    slashDismissedQuery !== slashQuery;
  useEffect(() => {
    setSlashIndex((prev) => (prev >= filteredSlashCommands.length ? 0 : prev));
  }, [filteredSlashCommands.length, slashQuery]);

  const commitSlash = useCallback(
    (command: SlashCommand) => {
      // Insert `/name ` and leave the caret after it so the user can
      // type the command's args (the transform runs at send).
      const next = `/${command.name} `;
      setText(next);
      setCaret(next.length);
      setSelStart(next.length);
      setSelEnd(next.length);
      setSlashIndex(0);
      onComposerTextChange?.(next);
      // Refocus on the next tick so the editor picks up the new buffer
      // before we hand focus back (same flow as `refocusEditor`, inlined
      // here because that callback is declared further down).
      queueMicrotask(() => {
        editorRef.current?.focus();
        webEditorRef.current?.focus();
      });
    },
    [onComposerTextChange],
  );

  // ── Saved messages ────────────────────────────────────────────────
  // Drop a saved message's body into the composer (both editors read the
  // single `text` prop, so a `setText` fills web + native — same flow as
  // `commitSlash`). Closes the popover and refocuses so the user can keep
  // typing / send immediately.
  const fillComposerFromSaved = useCallback(
    (msg: SavedMessage) => {
      const next = msg.text;
      setText(next);
      setCaret(next.length);
      setSelStart(next.length);
      setSelEnd(next.length);
      onComposerTextChange?.(next);
      setSavedMenuOpen(false);
      log.debug("composer.saved.fill", { len: next.length });
      queueMicrotask(() => {
        editorRef.current?.focus();
        webEditorRef.current?.focus();
      });
    },
    [onComposerTextChange],
  );

  const handleSaveNewMessage = useCallback((title: string, body: string) => {
    addSavedMessage({ text: body, ...(title.length > 0 ? { title } : {}) });
    log.debug("composer.saved.create", { hasTitle: title.length > 0 });
  }, []);

  const handleRemoveSaved = useCallback((id: string) => {
    removeSavedMessage(id);
  }, []);

  useEffect(() => {
    if (!slashOpen) return;
    if (typeof document === "undefined") return;
    const onKeyDownCapture = (e: KeyboardEvent): void => {
      const len = filteredSlashCommands.length;
      if (len === 0) return;
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          e.stopPropagation();
          setSlashIndex((i) => (i + 1) % len);
          break;
        case "ArrowUp":
          e.preventDefault();
          e.stopPropagation();
          setSlashIndex((i) => (i - 1 + len) % len);
          break;
        case "Enter":
        case "Tab":
          e.preventDefault();
          e.stopPropagation();
          setSlashIndex((i) => {
            const cmd = filteredSlashCommands[i];
            if (cmd !== undefined) commitSlash(cmd);
            return 0;
          });
          break;
        case "Escape":
          e.preventDefault();
          e.stopPropagation();
          setSlashDismissedQuery(slashQuery);
          break;
        default:
          break;
      }
    };
    document.addEventListener("keydown", onKeyDownCapture, true);
    return () =>
      document.removeEventListener("keydown", onKeyDownCapture, true);
  }, [slashOpen, filteredSlashCommands, commitSlash, slashQuery]);

  // Inline emoji popover state. The `Smile` button in the input pill
  // toggles this; the panel renders above the composer row when open
  // and clicking an emoji splices it into `text` at the caret. The
  // overlay itself comes from `ComposerEmojiOverlay` and is fed by the
  // curated `EMOJI_CATALOG` filtered against `emojiQuery`.
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [emojiQuery, setEmojiQuery] = useState("");
  const [isInputFocused, setIsInputFocused] = useState(false);
  // Anchor refs for the trigger-anchored popovers:
  //   emojiAnchorRef  → emoji / GIFs / Stickers popover (modal)
  //   attachAnchorRef → attachment menu popover (modal)
  // The format toolbar is rendered inline-absolute inside the
  // composer column (see `richStyles.floatingFormatToolbar`) so it
  // doesn't need a measured anchor.
  const emojiAnchorRef = useRef<PopoverAnchorHandle | null>(null);
  const attachAnchorRef = useRef<PopoverAnchorHandle | null>(null);
  // Editor focus handle. Toolbar buttons live outside the textarea
  // so clicking them blurs the input on web — we call `focus()`
  // after every toolbar / shortcut action so the textarea regains
  // focus and the new selection becomes visible (RN's `selection`
  // prop is a no-op while the input is blurred).
  const editorRef = useRef<RichTextEditorHandle | null>(null);
  // Separate ref for the web editor — its handle shape is a strict
  // subset of `RichTextEditorHandle` (only `focus()` overlaps for
  // the host's refocus-after-toolbar-action flow). We keep both refs
  // live and the platform branch below assigns to the right one.
  const webEditorRef = useRef<EnrichedRichTextEditorHandle | null>(null);
  // DOM node of the editor wrapper (web only), used by the floating
  // format toolbar to measure the live text selection. RN-Web forwards
  // the underlying element to callback refs; on native this resolves to
  // a View instance we ignore (native uses the pinned toolbar).
  const editorRootRef = useRef<HTMLElement | null>(null);
  const setEditorArea = useCallback((node: View | null) => {
    const el = node as unknown as HTMLElement | null;
    editorRootRef.current =
      el !== null && typeof el.getBoundingClientRect === "function" ? el : null;
  }, []);
  const refocusEditor = useCallback(() => {
    // Defer to the next tick so the new `text` + `selection` props
    // are committed before we hand focus back. Without this, the
    // textarea sees the old buffer and the caret snaps to the wrong
    // spot. Either ref may be live depending on platform branch;
    // we only call the one that's mounted.
    queueMicrotask(() => {
      editorRef.current?.focus();
      webEditorRef.current?.focus();
    });
  }, []);
  const emojiItems = useMemo<readonly ComposerEmojiOverlayItem[]>(
    () =>
      filterEmojiCatalog(EMOJI_CATALOG, emojiQuery).map((entry) => ({
        glyph: entry.code,
        shortcode: entry.name,
        group: entry.group,
      })),
    [emojiQuery],
  );

  // GIF tab state. Lives on the composer so the popover stays
  // controlled — the same query buffer survives tab switches and
  // the result list re-renders without re-fetching on every
  // emoji-grid change. When `gifProvider` is omitted these stay
  // unused and the unified overlay falls back to its emoji-only
  // path (GIFs tab dimmed).
  const [gifQuery, setGifQuery] = useState("");
  const [gifItems, setGifItems] = useState<readonly GifItem[]>([]);
  const gifRequestSeqRef = useRef(0);
  useEffect(() => {
    if (gifProvider === undefined || !showEmojiPicker) return;
    const seq = ++gifRequestSeqRef.current;
    const q = gifQuery.trim();
    const fetcher =
      q.length === 0
        ? gifProvider.trending({ limit: 24 })
        : gifProvider.search(q, { limit: 24 });
    void fetcher
      .then((items) => {
        // Reject stale responses if a newer query landed first.
        if (seq !== gifRequestSeqRef.current) return;
        setGifItems(items);
      })
      .catch((err) => {
        if (seq !== gifRequestSeqRef.current) return;
        log.warn("composer.gif.fetch.failed", { err: (err as Error).message });
        setGifItems([]);
      });
  }, [gifProvider, gifQuery, showEmojiPicker]);

  const handleSendGif = useCallback(
    (gif: GifItem) => {
      // Close the popover immediately so the user sees the
      // bubble land. The actual send is async; failure surfaces
      // through the host's `sendError` banner upstream.
      setShowEmojiPicker(false);
      setGifQuery("");
      log.debug("composer.gif.send", { gifId: gif.id });
      if (onSendGif === undefined) return;
      void (async () => {
        try {
          await onSendGif(gif);
        } catch (err) {
          log.error("composer.gif.send.failed", err as Error);
        }
      })();
    },
    [onSendGif],
  );

  // Stickers tab state. Mirrors the GIF wiring with its own query
  // buffer + request sequence so tab switches don't trample one
  // another's results.
  const [stickerQuery, setStickerQuery] = useState("");
  const [stickerItems, setStickerItems] = useState<readonly GifItem[]>([]);
  const stickerRequestSeqRef = useRef(0);
  useEffect(() => {
    if (stickerProvider === undefined || !showEmojiPicker) return;
    const seq = ++stickerRequestSeqRef.current;
    const q = stickerQuery.trim();
    const fetcher =
      q.length === 0
        ? stickerProvider.trending({ limit: 24 })
        : stickerProvider.search(q, { limit: 24 });
    void fetcher
      .then((items) => {
        if (seq !== stickerRequestSeqRef.current) return;
        setStickerItems(items);
      })
      .catch((err) => {
        if (seq !== stickerRequestSeqRef.current) return;
        log.warn("composer.sticker.fetch.failed", {
          err: (err as Error).message,
        });
        setStickerItems([]);
      });
  }, [stickerProvider, stickerQuery, showEmojiPicker]);

  const handleSendSticker = useCallback(
    (sticker: GifItem) => {
      setShowEmojiPicker(false);
      setStickerQuery("");
      log.debug("composer.sticker.send", { stickerId: sticker.id });
      if (onSendSticker === undefined) return;
      void (async () => {
        try {
          await onSendSticker(sticker);
        } catch (err) {
          log.error("composer.sticker.send.failed", err as Error);
        }
      })();
    },
    [onSendSticker],
  );

  // Inline voice-recording state machine. When `Mic` is tapped we
  // enter the `recording` phase which replaces the input pill with
  // `<ComposerVoiceBar />` (Figma `3125:3929`). After Pause the bar
  // transitions to `paused` (Figma `3139:301`) where the user can
  // send, restart, or discard. The actual audio capture is
  // host-owned via `onStartVoice` / `onSendVoiceMessage` /
  // `onCancelVoiceMessage`; this composer drives only the UI state
  // and rolls the live amplitude buffer.
  type VoicePhase = "idle" | "recording" | "paused";
  const [voicePhase, setVoicePhase] = useState<VoicePhase>("idle");
  const [recordingMs, setRecordingMs] = useState(0);
  const [amplitudes, setAmplitudes] = useState<number[]>([]);
  // Max bars retained in the rolling buffer. Sized generously so
  // wide composers stay full while keeping memory bounded — the
  // `<ComposerVoiceBar />` slices its own visible window from the
  // tail based on the measured waveform width, so this just caps
  // the upper bound to keep the buffer from growing unbounded on
  // long recordings.
  const AMPLITUDE_WINDOW = 256;
  const isRecording = voicePhase !== "idle";

  useEffect(() => {
    if (voicePhase !== "recording") {
      return undefined;
    }
    const startedAt = Date.now() - recordingMs;
    const tick = setInterval(() => {
      setRecordingMs(Date.now() - startedAt);
    }, 100);
    return () => clearInterval(tick);
    // We DON'T want `recordingMs` in the dep array — the effect
    // resets the offset to the latest value when phase flips back
    // to recording (resume from pause), but inside the interval we
    // read from the closed-over `startedAt`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voicePhase]);

  // Subscribe to live amplitude samples while recording. Each
  // sample appends to the rolling buffer; on pause/idle we tear
  // down the listener but KEEP the buffer so the paused-state
  // preview still shows the captured waveform.
  useEffect(() => {
    if (voicePhase !== "recording" || subscribeAmplitudes === undefined) {
      return undefined;
    }
    const unsub = subscribeAmplitudes((amplitude) => {
      const clamped = Math.max(0, Math.min(1, amplitude));
      setAmplitudes((prev) => {
        const next =
          prev.length >= AMPLITUDE_WINDOW
            ? prev.slice(prev.length - AMPLITUDE_WINDOW + 1)
            : prev.slice();
        next.push(clamped);
        return next;
      });
    });
    return () => {
      try {
        unsub();
      } catch (err) {
        log.warn("composer.voice.subscribeAmplitudes.cleanup", {
          err: (err as Error).message,
        });
      }
    };
  }, [voicePhase, subscribeAmplitudes]);

  const handleEmojiSelect = useCallback(
    (item: ComposerEmojiOverlayItem) => {
      const emoji = item.glyph;
      // Web path: route through the editor so the emoji replaces the
      // active `:foo` trigger window (when present) or inserts at the
      // live caret position inside the contenteditable.
      if (isWeb && webEditorRef.current !== null) {
        // Focus FIRST so the editor has a live caret — clicking an emoji
        // while the composer is unfocused otherwise inserts into a null
        // selection and silently no-ops (the reported bug). focus()
        // restores the last caret (or moves to the end), so insertEmoji
        // then lands the glyph.
        webEditorRef.current.focus();
        webEditorRef.current.insertEmoji(emoji);
        setShowEmojiPicker(false);
        setEmojiQuery("");
        setWebEmojiQuery(null);
        return;
      }
      // Native path — append at the caret, defaulting to the end of the
      // buffer when the input was never focused (caret stays 0 → end).
      const insertAt = isInputFocused ? caret : text.length;
      const before = text.slice(0, insertAt);
      const after = text.slice(insertAt);
      const next = `${before}${emoji}${after}`;
      setText(next);
      setCaret(insertAt + emoji.length);
      setShowEmojiPicker(false);
      setEmojiQuery("");
    },
    [isWeb, text, caret, isInputFocused],
  );

  // Mirror the editor's `:`-trigger state into the existing emoji
  // popover. Typing `:smi` → open popover with `smi` pre-queried;
  // closing the trigger (whitespace / Esc / blur) → close the
  // popover unless the user opened it manually via the Smile button.
  const triggeredEmojiOpenRef = useRef(false);
  useEffect(() => {
    if (!isWeb) return;
    if (webEmojiQuery !== null) {
      setShowEmojiPicker(true);
      setEmojiQuery(webEmojiQuery);
      triggeredEmojiOpenRef.current = true;
      return;
    }
    if (triggeredEmojiOpenRef.current) {
      triggeredEmojiOpenRef.current = false;
      setShowEmojiPicker(false);
      setEmojiQuery("");
    }
  }, [isWeb, webEmojiQuery]);

  const handleMicPress = useCallback(() => {
    setRecordingMs(0);
    setAmplitudes([]);
    setVoicePhase("recording");
    onStartVoice?.();
  }, [onStartVoice]);

  const handleCancelRecording = useCallback(() => {
    setVoicePhase("idle");
    setRecordingMs(0);
    setAmplitudes([]);
    onCancelVoiceMessage?.();
  }, [onCancelVoiceMessage]);

  // Pause toggles from recording → paused. The host's recorder
  // stops capturing on its side, but we hold onto the rolling
  // amplitude buffer so the paused-state UI keeps the visualization.
  const handleTogglePlayPause = useCallback(() => {
    setVoicePhase((prev) => {
      if (prev === "recording") {
        log.debug("composer.voice.pause", { durationMs: recordingMs });
        return "paused";
      }
      // From paused → resume recording. We don't drop the captured
      // amplitudes — fresh samples append to the rolling window.
      log.debug("composer.voice.resume", {});
      return "recording";
    });
  }, [recordingMs]);

  // Mic-icon on the right side during paused state — restart
  // recording from scratch. Discards the captured buffer.
  const handleRestartRecording = useCallback(() => {
    log.debug("composer.voice.restart", {});
    setRecordingMs(0);
    setAmplitudes([]);
    setVoicePhase("recording");
  }, []);

  const handleSendRecording = useCallback(() => {
    const elapsed = recordingMs;
    setVoicePhase("idle");
    setAmplitudes([]);
    setRecordingMs(0);
    log.debug("composer.voice.send", { durationMs: elapsed });
    if (onSendVoiceMessage === undefined) return;
    void (async () => {
      try {
        await onSendVoiceMessage(elapsed);
      } catch (err) {
        log.error("composer.voice.send.failed", err as Error);
      }
    })();
  }, [recordingMs, onSendVoiceMessage]);

  // Format toggle. Applies the `RichTextFormat` to the current
  // text + selection via the shared action module, then pushes the
  // new buffer + selection back into state. The textarea picks up
  // the new selection from `pendingSelection` on the next render
  // (see the editor's `selection` prop).
  const toggleFormat = useCallback(
    (format: RichTextFormat) => {
      log.debug("composer.format.toggle", { format });
      if (isWeb) {
        // On web the enriched editor owns the buffer; route the
        // toggle through the ref so it operates on the live DOM
        // selection. The editor will emit a fresh markdown string
        // via `onChangeMarkdown` which keeps the host's `text` in
        // sync.
        webEditorRef.current?.toggleFormat(format);
        refocusEditor();
        return;
      }
      const result = applyFormatToggle(format, text, selStart, selEnd);
      setText(result.text);
      setSelStart(result.selStart);
      setSelEnd(result.selEnd);
      setCaret(result.selEnd);
      setPendingSelection({ start: result.selStart, end: result.selEnd });
      refocusEditor();
    },
    [isWeb, text, selStart, selEnd, refocusEditor],
  );

  // Bullet / numbered-list toggle. Cmd+Shift+8 / Cmd+Shift+7 in
  // the editor route here; the toolbar exposes these via the same
  // callback so toolbar and shortcut share one code path.
  const toggleListKind = useCallback(
    (kind: "bulleted" | "numbered") => {
      log.debug("composer.list.toggle", { kind });
      if (isWeb) {
        if (kind === "numbered") webEditorRef.current?.toggleNumberedList();
        else webEditorRef.current?.toggleBulletList();
        refocusEditor();
        return;
      }
      const result =
        kind === "numbered"
          ? toggleNumberedList(text, selStart, selEnd)
          : toggleLinePrefix(text, selStart, selEnd, "- ");
      setText(result.text);
      setSelStart(result.selStart);
      setSelEnd(result.selEnd);
      setCaret(result.selEnd);
      setPendingSelection({ start: result.selStart, end: result.selEnd });
      refocusEditor();
    },
    [isWeb, text, selStart, selEnd, refocusEditor],
  );

  // Code-block language selection (web only) — routes the chosen
  // language through the editor ref so it lands on the `<pre>` the
  // caret sits in, then refocuses so the toolbar stays live.
  const setCodeLanguage = useCallback(
    (language: string) => {
      log.debug("composer.code.language", { language });
      webEditorRef.current?.setCodeBlockLanguage(language);
      refocusEditor();
    },
    [refocusEditor],
  );

  // Link insertion (Cmd+Shift+U). Wraps the selection in
  // `[label](url)` and places the caret on the URL token so the
  // user can paste the destination immediately.
  const insertLinkAtSelection = useCallback(() => {
    if (isWeb) {
      // The enriched editor's link command parks the caret on the
      // URL placeholder so the user can type/paste the target.
      webEditorRef.current?.setLink("https://");
      refocusEditor();
      log.debug("composer.link.insert", {});
      return;
    }
    const result = insertLink(text, selStart, selEnd);
    setText(result.text);
    setSelStart(result.selStart);
    setSelEnd(result.selEnd);
    setCaret(result.selEnd);
    setPendingSelection({ start: result.selStart, end: result.selEnd });
    refocusEditor();
    log.debug("composer.link.insert", {});
  }, [isWeb, text, selStart, selEnd, refocusEditor]);

  // Single dispatcher for the editor's `onShortcut` callback.
  // The editor resolves the key event to an action; we apply it
  // here using the host-owned text + selection state.
  const handleShortcut = useCallback(
    (
      action:
        | { readonly kind: "format"; readonly format: RichTextFormat }
        | { readonly kind: "list"; readonly listKind: "numbered" | "bulleted" }
        | { readonly kind: "link" },
    ) => {
      switch (action.kind) {
        case "format":
          toggleFormat(action.format);
          return;
        case "list":
          toggleListKind(action.listKind);
          return;
        case "link":
          insertLinkAtSelection();
          return;
      }
    },
    [toggleFormat, toggleListKind, insertLinkAtSelection],
  );

  // Editor-driven selection range updates — the active-format set
  // tracks the caret.
  const handleSelectionRangeChange = useCallback(
    (start: number, end: number) => {
      setSelStart(start);
      setSelEnd(end);
      // Clear any pending imperative selection once the textarea
      // has acknowledged the new range — otherwise typing right
      // after a toolbar click would keep snapping the caret back.
      setPendingSelection(null);
    },
    [],
  );

  const handleSend = useCallback(async () => {
    const trimmed = text.trim();
    // Allow an empty body when the host has staged attachments — the
    // host flushes the tray on send (text + attachments together).
    if ((!trimmed && !hasStagedAttachments) || isSending) {
      return;
    }
    setIsSending(true);
    // Full composer reset. The editor itself remounts via the
    // `key:length` bump in `EnrichedRichTextEditor`; we still need
    // to drop every host-side artifact so nothing carries into the
    // fresh buffer: caret + selection ranges, active-format chips,
    // mention / emoji / link popovers, the emoji picker overlay.
    setText("");
    setCaret(0);
    setSelStart(0);
    setSelEnd(0);
    setPendingSelection({ start: 0, end: 0 });
    setEditorState("collapsed");
    setWebActive({
      formats: new Set(),
      bulletList: false,
      numberedList: false,
      link: false,
      codeLanguage: null,
      selectionEmpty: true,
    });
    setWebMentionTrigger(null);
    setWebEmojiQuery(null);
    setLinkEdit(null);
    setShowEmojiPicker(false);
    setEmojiQuery("");
    setGifQuery("");
    setStickerQuery("");
    triggeredEmojiOpenRef.current = false;
    try {
      if (editingMessage != null && onSubmitEdit !== undefined) {
        // EDIT: replace the target message's text instead of sending a new one.
        await onSubmitEdit(trimmed);
        editingIdRef.current = null;
        onCancelEdit?.();
        log.debug("composer.edit", { len: trimmed.length });
      } else {
        // Slash commands rewrite the outgoing body. `undefined` = not a
        // command (send `trimmed` verbatim); a string = send the rewrite;
        // `null` = the command handled itself, send nothing.
        const commandBody = runSlashCommand(slashCommandsAll, trimmed);
        if (commandBody === null) {
          log.debug("composer.slash.noop", {});
        } else {
          const body = commandBody ?? trimmed;
          await onSend(body, replyTo?.id);
          if (replyTo) {
            onCancelReply?.();
          }
          log.debug("composer.send", {
            len: body.length,
            slash: commandBody !== undefined,
            hasReply: replyTo !== null && replyTo !== undefined,
          });
        }
      }
    } catch (error) {
      setText(trimmed);
      log.error("composer.send.failed", error as Error);
    } finally {
      setIsSending(false);
    }
  }, [
    text,
    isSending,
    hasStagedAttachments,
    onSend,
    replyTo,
    onCancelReply,
    editingMessage,
    onSubmitEdit,
    onCancelEdit,
    slashCommandsAll,
  ]);

  // ── Schedule message ──────────────────────────────────────────────
  // Cross-platform (web + react-native-macos): the modal, picker, popover,
  // and scheduled bar are all RN-primitive. Native uses the in-memory
  // client scheduler (delivers while the app is open); a host can wire
  // `onScheduleMessage` for backend-backed delivery on either platform.
  const conversationId = draftKey;
  const scheduleEnabled =
    showSchedule !== false &&
    editingMessage == null &&
    (conversationId !== undefined || onScheduleMessage !== undefined);
  // Saved-messages affordance replaces the empty-composer send button with a
  // bookmark that opens the quick-reply popover. Not shown while editing.
  const savedEnabled = showSavedMessages !== false && editingMessage == null;
  // When a host override is wired it owns persistence + surfacing, so the
  // built-in client store is bypassed entirely.
  const usesClientScheduler =
    onScheduleMessage === undefined && conversationId !== undefined;

  const [scheduledList, setScheduledList] = useState<ScheduledMessage[]>(() =>
    usesClientScheduler ? listScheduledMessages(conversationId) : [],
  );
  useEffect(() => {
    if (!usesClientScheduler) {
      setScheduledList([]);
      return;
    }
    setScheduledList(listScheduledMessages(conversationId));
    return subscribeScheduled(() =>
      setScheduledList(listScheduledMessages(conversationId)),
    );
  }, [usesClientScheduler, conversationId]);

  // Register this conversation's send fn with the client scheduler so due
  // messages deliver while the composer is mounted. A ref keeps the
  // registration stable across `onSend` identity changes.
  const onSendRef = useRef(onSend);
  onSendRef.current = onSend;
  useEffect(() => {
    if (!usesClientScheduler) return;
    return registerScheduledSender(conversationId, (t, r) =>
      onSendRef.current(t, r),
    );
  }, [usesClientScheduler, conversationId]);

  // Which pending list drives the bar: the built-in client store when we own
  // scheduling, else the host-provided `scheduledMessages` (backend-backed).
  const barScheduled: readonly ScheduledMessage[] = usesClientScheduler
    ? scheduledList
    : (scheduledMessages ?? []);

  const handleCancelScheduled = useCallback(
    (id: string) => {
      if (usesClientScheduler) {
        cancelScheduledMessage(id);
      } else {
        onCancelScheduled?.(id);
      }
    },
    [usesClientScheduler, onCancelScheduled],
  );

  const handleSchedule = useCallback(
    async (sendAt: Date) => {
      setScheduleMenuOpen(false);
      setScheduleModalOpen(false);
      const trimmed = text.trim();
      if (trimmed.length === 0) return;
      const commandBody = runSlashCommand(slashCommandsAll, trimmed);
      if (commandBody === null) return; // no-op slash command — nothing to queue
      const body = commandBody ?? trimmed;
      const sendAtMs = sendAt.getTime();
      const replyId = replyTo?.id;
      // Clear the composer immediately — the message is now queued.
      setText("");
      setCaret(0);
      setSelStart(0);
      setSelEnd(0);
      setPendingSelection({ start: 0, end: 0 });
      setWebActive({
        formats: new Set(),
        bulletList: false,
        numberedList: false,
        link: false,
        codeLanguage: null,
        selectionEmpty: true,
      });
      setWebMentionTrigger(null);
      setWebEmojiQuery(null);
      setLinkEdit(null);
      setShowEmojiPicker(false);
      if (replyTo) onCancelReply?.();
      try {
        if (onScheduleMessage !== undefined) {
          await onScheduleMessage(
            body,
            new Date(sendAtMs).toISOString(),
            replyId,
          );
        } else if (conversationId !== undefined) {
          scheduleMessage({
            conversationId,
            text: body,
            ...(replyId !== undefined ? { replyTo: replyId } : {}),
            sendAtMs,
          });
        }
        log.debug("composer.schedule", { at: sendAtMs });
      } catch (error) {
        setText(body); // restore so the user can retry
        log.error("composer.schedule.failed", error as Error);
      }
    },
    [
      text,
      slashCommandsAll,
      replyTo,
      onCancelReply,
      onScheduleMessage,
      conversationId,
    ],
  );

  const handleAttachToggle = useCallback(() => {
    setMenuOpen((open) => {
      log.debug("composer.attach.menu", { open: !open });
      return !open;
    });
  }, []);

  const handleMenuSelect = useCallback(
    async (kind: ChatRoomAttachMenuKind) => {
      setMenuOpen(false);
      log.debug("composer.attach.select", { kind });
      switch (kind) {
        case "camera":
          if (onCaptureCamera !== undefined) {
            await onCaptureCamera();
            return;
          }
          // No camera on this host — fall back to the image picker.
          if (onPickImages !== undefined) {
            await onPickImages();
          }
          return;
        case "photo":
          if (onPickImages !== undefined) {
            await onPickImages();
            return;
          }
          if (Platform.OS === "web") {
            log.warn("composer.attach.photo.unwired.web", {});
            return;
          }
          return;
        case "doc":
          if (onPickDoc !== undefined) {
            await onPickDoc();
          }
          return;
        case "poll":
          // Explicit override (threads) wins over the shell context so the
          // poll targets the thread, not the parent conversation.
          if (onOpenPoll !== undefined) onOpenPoll();
          else shellHandlers?.openPollComposer();
          return;
        case "event":
          // Opens the meeting composer (create), routed through the shell so
          // the host owns the modal overlay + `com.ping.meeting` send.
          if (onOpenEvent !== undefined) onOpenEvent();
          else shellHandlers?.openMeetingComposer?.();
          return;
        case "location":
          if (onOpenLocation !== undefined) onOpenLocation();
          else shellHandlers?.openShareLocation();
          return;
        case "games":
          // Opens the game-kind picker (coinflip/dice/rps/…) which then
          // emits a GAME_CREATE envelope. Routed through the shell so the
          // host owns the picker overlay + send.
          if (onOpenGames !== undefined) onOpenGames();
          else shellHandlers?.openGamePicker?.();
          return;
        case "voice":
          onStartVoice?.();
          return;
      }
    },
    [
      onPickImages,
      onPickDoc,
      onCaptureCamera,
      onStartVoice,
      shellHandlers,
      onOpenPoll,
      onOpenEvent,
      onOpenLocation,
      onOpenGames,
    ],
  );

  const canSend =
    (text.trim().length > 0 || hasStagedAttachments) && !isSending && !disabled;
  // "Has rich-block content" — drives the pill → rounded-rect chrome
  // switch and the items-end alignment. Plain `\n` is not enough:
  //   - Multi-paragraph text DOES emit `\n` (good).
  //   - A SINGLE bullet / numbered / quote / code-block line does
  //     not emit `\n` but visually it's a rich block.
  // We catch both: literal newlines for multi-line text AND
  // single-line block markers for the standalone block case. On
  // web we also union the editor's reported block flags, which
  // are authoritative for the live DOM (covers edge cases like
  // empty code blocks or list items whose markdown emit hasn't
  // landed yet).
  const SINGLE_BLOCK_LINE_RE = /^(?:[-*+>]\s|\d+\.\s|```)/m;
  const hasNewLine =
    text.includes("\n") ||
    SINGLE_BLOCK_LINE_RE.test(text) ||
    (isWeb &&
      (webActive.bulletList ||
        webActive.numberedList ||
        webActive.formats.has("quote") ||
        webActive.formats.has("codeBlock")));

  // Format-toolbar visibility. The toolbar is a SELECTION affordance:
  // it only appears while the user has an actual range of text
  // highlighted — never on mere focus or a caret sitting in the buffer.
  // This mirrors the Element/Matrix lexical composer, whose floating
  // toolbar mounts only on a non-collapsed selection (see
  // `FloatingRichTextToolbar`).
  //   - Web: the contenteditable engine reports `selectionEmpty` in its
  //     active-format stream, so a real highlight is `!selectionEmpty`.
  //   - Native: the host tracks the `<TextInput>` range, so a highlight
  //     is `selStart !== selEnd`.
  // Two carve-outs keep the toolbar mounted so its own affordances don't
  // self-dismiss: while the link-edit popover is open, and while the
  // caret is inside a code block (so the language selector stays
  // reachable even without a selection).
  const hasTextSelection = isWeb
    ? !webActive.selectionEmpty
    : selStart !== selEnd;
  const isCodeBlockActive = isWeb && webActive.formats.has("codeBlock");
  const showFormatToolbar =
    !isRecording &&
    (hasTextSelection || linkEdit !== null || isCodeBlockActive);

  // Shared toolbar body — rendered inside the floating bubble (web) or
  // the pinned chip (native). Built once so both paths stay in lockstep.
  const formatToolbarNode = (
    <FormatOptions
      activeFormats={activeFormats}
      onToggle={toggleFormat}
      isBulletListActive={isWeb ? webActive.bulletList : false}
      isNumberedListActive={isWeb ? webActive.numberedList : false}
      isLinkActive={isWeb ? webActive.link : false}
      onToggleBulletList={() => toggleListKind("bulleted")}
      onToggleNumberedList={() => toggleListKind("numbered")}
      onInsertLink={insertLinkAtSelection}
      codeBlockActive={isWeb && activeFormats.has("codeBlock")}
      codeLanguage={isWeb ? webActive.codeLanguage : null}
      onSelectCodeLanguage={setCodeLanguage}
      testID="composer-format-toolbar"
    />
  );

  return (
    <View
      style={styles.container}
      {...(containerClassName !== undefined
        ? { className: containerClassName }
        : {})}
      testID={testID}
    >
      {barScheduled.length > 0 ? (
        <ScheduledMessagesBar
          messages={barScheduled}
          onCancel={handleCancelScheduled}
          testID="composer-scheduled-bar"
        />
      ) : null}
      {editingMessage != null && (
        // Editing chip — mirrors the reply chip. Pencil + "Editing message" +
        // the target's preview, with a cancel affordance → `onCancelEdit`.
        <View className="mx-sm flex-row items-center rounded-[12px] bg-light-surface-secondary dark:bg-dark-surface-elevated pl-[12px] pr-[12px] pt-[8px] pb-[8px]">
          <View className="flex-1 flex-col gap-[4px]">
            <View className="flex-row items-center gap-[6px]">
              <Pencil size={14} strokeWidth={2} color="#537EFF" />
              <Text className="font-sans text-[14px] font-bold text-light-text-primary dark:text-white">
                Editing message
              </Text>
            </View>
            <PreviewLabel
              text={formatMessagePreview(editingMessage.body) ?? editingMessage.body}
              color={theme.colors.text.secondary}
              iconSize={14}
              numberOfLines={1}
              className="font-sans text-[14px] font-normal"
            />
          </View>
          <Pressable
            onPress={onCancelEdit}
            className="ml-[12px] p-[2px] active:opacity-60"
            accessibilityRole="button"
            accessibilityLabel="Cancel edit"
          >
            <X size={18} strokeWidth={2} color="#B5BAC3" />
          </Pressable>
        </View>
      )}
      {replyTo && (
        // Reply-to chip — Figma `2804:27420`. Container `#222429`, 12px
        // radius, 12/8 padding; a row with the reply content on the left
        // and a close affordance on the right.
        <View className="mx-sm flex-row items-center rounded-[12px] bg-light-surface-secondary dark:bg-dark-surface-elevated pl-[12px] pr-[12px] pt-[8px] pb-[8px]">
          {/* Media reply → a real thumbnail (iOS ReplyQuoteThumbnail parity). */}
          {replyToThumbnailUri !== undefined && replyToThumbnailUri.length > 0 ? (
            <View className="mr-[10px] h-[36px] w-[36px] overflow-hidden rounded-[8px] bg-[#3A3D44]">
              <Image source={{ uri: replyToThumbnailUri }} className="h-[36px] w-[36px]" />
              {replyToThumbnailKind === "video" ? (
                <View className="absolute inset-0 items-center justify-center bg-black/25">
                  <Play size={12} strokeWidth={2} color="#FFFFFF" fill="#FFFFFF" />
                </View>
              ) : null}
            </View>
          ) : null}
          <View className="flex-1 flex-col gap-[4px]">
            {/* Top line — avatar (18×18, 6px radius) + sender name. */}
            <View className="flex-row items-center gap-[6px]">
              <View className="h-[18px] w-[18px] overflow-hidden rounded-[6px] bg-[#3A3D44] items-center justify-center">
                {replyToAvatar?.imageUri !== undefined &&
                replyToAvatar.imageUri.length > 0 ? (
                  <Image
                    source={{ uri: replyToAvatar.imageUri }}
                    className="h-[18px] w-[18px]"
                  />
                ) : (
                  <Text className="font-sans text-[9px] font-semibold text-white">
                    {replyToAvatar?.initials ?? ""}
                  </Text>
                )}
              </View>
              <Text
                numberOfLines={1}
                className="font-sans text-[14px] font-bold text-light-text-primary dark:text-white"
              >
                {replyToSenderName ?? "Replying to"}
              </Text>
            </View>
            {/* Second line — replied message preview, single line. Decode the
                envelope to a human preview (never raw `{"kind":...}` JSON) and
                render its kind glyph as a lucide icon. */}
            <PreviewLabel
              text={formatMessagePreview(replyTo.body) ?? replyTo.body}
              color={theme.colors.text.secondary}
              iconSize={14}
              numberOfLines={1}
              className="font-sans text-[14px] font-normal"
            />
          </View>
          <Pressable
            onPress={onCancelReply}
            className="ml-[12px] p-[2px] active:opacity-60"
            accessibilityRole="button"
            accessibilityLabel="Cancel reply"
          >
            <X size={18} strokeWidth={2} color="#B5BAC3" />
          </Pressable>
        </View>
      )}
      {/*
        Attachment menu — Figma `2804:26754` "Action menu". Anchored
        popover above the `+` button. Renders all seven entries
        (Camera / Photos / Files / Location / Poll / Event / Games)
        per the design. The popover dismisses on backdrop tap and on
        any menu selection.
      */}
      <Popover
        visible={menuOpen && showAttachmentMenu}
        anchorRef={attachAnchorRef}
        onClose={() => setMenuOpen(false)}
        placement="top"
        align="center"
        gap={8}
      >
        <AttachmentMenu
          onSelect={handleMenuSelect}
          {...(attachMenuKinds !== undefined ? { kinds: attachMenuKinds } : {})}
        />
      </Popover>
      {/*
        Mention popover. Renders above the input pill when the user
        types `@` and the host supplied `mentionCandidates`. Visual ref:
        Figma `2918:10419` (Mention section) → candidate row mirrors
        `tag-you` (`2918:10420`).
      */}
      {/*
        Mention popover.
          - Web path: anchored-floating panel positioned at the @
            trigger's caret rect (`position: fixed`). Floats above
            the entire UI, doesn't take flow space, doesn't push
            the composer chrome around.
          - Native path: keeps the inline rendering since the
            `<TextInput>`-backed editor can't surface a DOM rect.
      */}
      {mentionCandidates !== undefined &&
      mentionCandidates.length > 0 &&
      isWeb ? (
        webMentionTrigger !== null &&
        (webMentionTrigger.anchorRect ?? lastMentionAnchorRef.current) !==
          null ? (
          // Portal'd to document.body so the popover escapes any
          // composer-side stacking / overflow / z-index ceiling.
          // RN-Web wraps every View in a `CssInteropComponent`
          // div which establishes its own stacking context; inside
          // that, even `position: fixed` can't render above hosts
          // that paint later. The portal moves the whole subtree
          // out of the composer column entirely.
          <WebPortal>
            {/*
              Plain `<div>` wrapper — `position: fixed` on an RN-Web
              `<View>` can get filtered/normalized by the styling
              layer. A raw HTML element bypasses that path entirely
              and renders exactly the CSS we ask for. The
              MentionPicker inside is still a `<View>`-based RN
              component, which RN-Web happily mounts inside a
              native `<div>` parent.
            */}
            {(() => {
              const PANEL_HEIGHT = 320;
              const PANEL_MIN_WIDTH = 280;
              // Hug the `@` caret. The format toolbar no longer sits
              // pinned above the pill (it floats at the selection now),
              // so the picker can anchor tightly to the trigger character
              // — a small gap above the caret, matching a typical
              // autocomplete dropdown.
              const GAP_ABOVE = 6;
              // Below-placement (flip) only needs a small visual gap.
              const GAP_BELOW = 6;
              const rect =
                webMentionTrigger.anchorRect ?? lastMentionAnchorRef.current!;
              const vw =
                typeof window !== "undefined" ? window.innerWidth : 1024;
              const vh =
                typeof window !== "undefined" ? window.innerHeight : 768;
              // The composer sits at the bottom of the chat, so the caret
              // is almost always in the lower half → place the popover
              // ABOVE it. CRITICAL: pin the popover's BOTTOM just above the
              // caret (don't reserve a fixed PANEL_HEIGHT from the top) so a
              // short candidate list hugs the composer and grows upward —
              // the old `top = caretY - 320` math made a 1-2 item list
              // float in the MIDDLE of the screen.
              const placeAbove = rect.y > vh / 2;
              let left = rect.x;
              if (left + PANEL_MIN_WIDTH > vw - 12) {
                left = Math.max(12, vw - PANEL_MIN_WIDTH - 12);
              }
              if (left < 12) left = 12;
              const posStyle: React.CSSProperties = placeAbove
                ? { bottom: Math.max(12, vh - rect.y + GAP_ABOVE) }
                : { top: rect.y + rect.height + GAP_BELOW };
              return (
                <>
                  {/*
                    Transparent click-outside backdrop. Single tap
                    anywhere outside the popover dismisses it,
                    matching native popover semantics. Stops short
                    of being a modal (no event blocking on the
                    composer pill) so typing keeps working.
                  */}
                  <div
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setWebMentionTrigger(null);
                    }}
                    style={{
                      position: "fixed",
                      inset: 0,
                      background: "transparent",
                      zIndex: 9999,
                    }}
                  />
                  <div
                    data-testid="composer-mention-popover"
                    style={{
                      position: "fixed",
                      left,
                      ...posStyle,
                      maxHeight: PANEL_HEIGHT,
                      minWidth: PANEL_MIN_WIDTH,
                      zIndex: 10000,
                      boxShadow:
                        "0 10px 28px rgba(0,0,0,0.22), 0 2px 6px rgba(0,0,0,0.16)",
                      borderRadius: 12,
                      overflow: "hidden",
                    }}
                    onMouseDown={(e) => {
                      // Don't let mousedown on the picker steal
                      // focus from the editor — same trick the
                      // toolbar uses.
                      e.preventDefault();
                    }}
                  >
                    <MentionPicker
                      open={true}
                      candidates={webMentionCandidates}
                      query={webMentionQuery ?? ""}
                      selectedIndex={webMentionIndex}
                      onSelect={(candidate) => {
                        webEditorRef.current?.insertMention(
                          candidate.displayName,
                          candidate.userId,
                        );
                        setWebMentionTrigger(null);
                        setWebMentionIndex(0);
                      }}
                      onClose={() => setWebMentionTrigger(null)}
                    />
                  </div>
                </>
              );
            })()}
          </WebPortal>
        ) : null
      ) : mentionCandidates !== undefined ? (
        <View className="px-xl pb-xs">
          <MentionPicker
            open={mentions.open}
            candidates={mentions.filteredCandidates}
            query={mentions.query}
            selectedIndex={mentions.selectedIndex}
            onSelect={(candidate) => {
              const next = mentions.insertMention(candidate, text, caret);
              setText(next.text);
              setCaret(next.caret);
            }}
            onClose={mentions.dismiss}
          />
        </View>
      ) : null}
      {/*
        Emoji / GIFs / Stickers popover. Rendered as a true anchored
        popover above the Smile trigger inside the input pill so the
        overlay stays centered on the button regardless of the
        composer's width. Closes on backdrop press or the overlay's
        internal close affordance.
      */}
      <Popover
        visible={showEmojiPicker}
        anchorRef={emojiAnchorRef}
        onClose={() => {
          setShowEmojiPicker(false);
          setEmojiQuery("");
          setGifQuery("");
        }}
        placement="top"
        align="center"
        // The emoji / GIF / sticker picker must ALWAYS open upward from the
        // composer — never flip below (which would push it off the bottom of
        // the chat behind the composer). On a short viewport it clamps to the
        // top edge instead of flipping.
        flip={false}
        testID="composer-emoji-popover"
      >
        <ComposerEmojiOverlay
          query={emojiQuery}
          items={emojiItems}
          onChangeQuery={setEmojiQuery}
          onSelect={handleEmojiSelect}
          onClose={() => {
            setShowEmojiPicker(false);
            setEmojiQuery("");
            setGifQuery("");
          }}
          testID="composer-emoji-overlay"
          {...(showGifs && gifProvider !== undefined && onSendGif !== undefined
            ? {
                gifItems,
                gifQuery,
                onChangeGifQuery: setGifQuery,
                onSelectGif: handleSendGif,
              }
            : {})}
          {...(showStickers &&
          stickerProvider !== undefined &&
          onSendSticker !== undefined
            ? {
                stickerItems,
                stickerQuery,
                onChangeStickerQuery: setStickerQuery,
                onSelectSticker: handleSendSticker,
              }
            : {})}
        />
      </Popover>
      {/*
        Format toolbar — Figma `2829:4379` / `2829:4191`.
          - WEB: a floating "bubble" that portals ABOVE the highlighted
            text (like Medium / Notion / the Element lexical composer's
            `FloatingRichTextToolbar`). It only appears on a non-collapsed
            selection, settles so it doesn't flicker mid-drag, and follows
            the selection rect. `FloatingFormatToolbar` owns visibility +
            positioning; we just hand it the shared `<FormatOptions />`.
          - NATIVE: the DOM-selection anchor isn't available over a
            `<TextInput>`, so we keep the pinned chip above the composer
            row, gated on a real selection (`showFormatToolbar`).
      */}
      {isWeb ? (
        <FloatingFormatToolbar
          editorRootRef={editorRootRef}
          disabled={disabled}
          // Keep the bar mounted (even with a collapsed selection) while
          // the link editor is open. Code blocks get their own always-
          // visible language chip (see `CodeLanguagePickerOverlay`), so
          // the selection toolbar no longer needs to linger for them.
          popoverOpen={linkEdit !== null}
          testID="composer-format-toolbar-floating"
        >
          {formatToolbarNode}
        </FloatingFormatToolbar>
      ) : showFormatToolbar ? (
        <View
          // Pinned format toolbar (native) — absolutely positioned above
          // the composer row. `bottom` tracks the measured composer-row
          // height so the chip slides UP as the input wraps. `position`,
          // `bottom`, `left`, `zIndex` + `pointerEvents` can't be
          // expressed via NativeWind, so they stay in `style`.
          style={{
            bottom: composerRowHeight - 2,
            left: 24 + 30 + 8,
            zIndex: 10,
            position: "absolute",
            pointerEvents: "box-none" as const,
          }}
        >
          {formatToolbarNode}
        </View>
      ) : null}
      {/*
        Link-edit popover. Mounted at the composer root with
        `position: absolute` so it can anchor at viewport-coordinate
        rects regardless of the input pill's overflow / clipping.
        Slack-style: clicking a link inside the editor opens this
        card instead of navigating.
      */}
      {isWeb && linkEdit !== null ? (
        <WebPortal>
          <LinkEditPopover
            visible={true}
            initialUrl={linkEdit.url}
            initialLabel={linkEdit.label}
            anchorRect={
              linkEdit.anchorRect !== null
                ? {
                    x: linkEdit.anchorRect.x,
                    y: linkEdit.anchorRect.y,
                    width: linkEdit.anchorRect.width,
                    height: linkEdit.anchorRect.height,
                  }
                : null
            }
            onSave={handleLinkEditSave}
            onRemove={handleLinkEditRemove}
            onDismiss={handleLinkEditDismiss}
            testID="composer-link-edit-popover"
          />
        </WebPortal>
      ) : null}
      {/*
        Code-block language picker — opened by clicking a code block's
        always-visible language chip. Commits via the editor's
        `setCodeBlockLanguage` (the chip click parked the caret in the
        block, so it targets the right one).
      */}
      {isWeb && codeLangCtx !== null ? (
        <CodeLanguagePickerOverlay
          anchorRect={codeLangCtx.anchorRect}
          currentLanguage={codeLangCtx.language}
          onSelect={(id) => {
            webEditorRef.current?.setCodeBlockLanguage(
              id === "plaintext" ? "" : id,
            );
            setCodeLangCtx(null);
          }}
          onClose={() => setCodeLangCtx(null)}
          testID="composer-code-language-picker"
        />
      ) : null}
      {/*
        Slash-command picker. Anchored just above the composer row (left
        edge aligned with the row's `px-xl` inset). Opens while the user
        is typing `/name` at the start of the composer; keyboard nav is
        handled by the document-capture listener above, tap by the picker.
      */}
      {slashOpen ? (
        <View
          style={{
            position: "absolute",
            bottom: composerRowHeight + 6,
            left: 24,
            zIndex: 30,
          }}
        >
          <SlashCommandPicker
            commands={filteredSlashCommands}
            selectedIndex={slashIndex}
            onSelect={commitSlash}
            testID="composer-slash-picker"
          />
        </View>
      ) : null}
      {/*
        Outer composer row — Figma `2804:26702` (typing/default) and
        `2804:26714` (typing/focus). Both share:
          - paddings L24 / R24 / T8 / B16
          - itemSpacing 8
          - fill `#11131A` (dark) / `surface.primary` (light)

        While voice recording is active (`voicePhase !== 'idle'`) the
        row replaces its full contents with `<ComposerVoiceBar />`
        (Figma `3125:3929` / `3139:301`), which carries its own
        trash / pause / mic / send affordances.
      */}
      <View
        // Figma `2804:26702` outer row: paddings L24 / R24 / T8 / B16,
        // itemSpacing 8, items aligned to the BOTTOM so the chrome
        // (attach + send) stays anchored to the input pill's bottom
        // edge when the textarea grows multi-line.
        onLayout={handleComposerRowLayout}
        className={`
          flex flex-row ${hasNewLine ? "items-end" : "items-center"} gap-sm
          ${denseComposerPadding ? "" : "pr-xl pl-lg pt-sm pb-lg"}
          bg-light-surface-primary dark:bg-[#11131A] ${containerContentClassName}`}
      >
        {isRecording ? (
          <ComposerVoiceBar
            phase={voicePhase === "paused" ? "paused" : "recording"}
            elapsedMs={recordingMs}
            amplitudes={amplitudes}
            progress={0}
            onCancel={handleCancelRecording}
            onTogglePlayPause={handleTogglePlayPause}
            onRestartRecording={handleRestartRecording}
            onSend={handleSendRecording}
            testID="composer-voice-bar"
          />
        ) : (
          <>
            {/*
              Left slot — circular attach button. Figma `2804:26703`
              (Frame 1000004367): 30×30, pill radius. Inner lucide
              `Plus` glyph at 20 / strokeWidth 2. Wrapped in a
              `<PopoverAnchor>` so the attachment menu can center
              above the button.
            */}
            {showAttachmentMenu ? (
              <PopoverAnchor ref={attachAnchorRef}>
                <Pressable
                  onPress={handleAttachToggle}
                  accessibilityRole="button"
                  accessibilityLabel="Attach"
                  accessibilityState={{ expanded: menuOpen }}
                  disabled={disabled || isSending}
                  // Figma `2804:26703` — 30×30 pill, no fill. Opacity
                  // drop when the composer is disabled / sending.
                  className={`
                    w-[30px] h-[30px] rounded-pill items-center justify-center
                    ${disabled || isSending ? "opacity-50" : ""}
                  `}
                  testID="composer-attach-button"
                >
                  <Plus
                    size={20}
                    strokeWidth={2}
                    color={theme.colors.text.secondary}
                  />
                </Pressable>
              </PopoverAnchor>
            ) : null}
            {/*
              Center pill — Figma `2804:26707` (Search): 40 tall, pill
              radius, paddings L10 R10, itemSpacing 10, dark fill
              `#222429` / light `surface.secondary` (`#F5F5F7`).
              Trailing-end of the pill carries the emoji glyph — `Smile`
              lucide icon — so the affordance lives INSIDE the pill the
              way Figma `2804:26707` shows.
            */}
            <View
              // Figma `2804:26707` — input pill: flex-1, 40 tall,
              // padding L10 R10. `borderRadius` swaps between pill
              // (single line) and 14px (multi-line) so the chrome
              // reads as a soft rectangle when the textarea wraps.
              // Background color stays inline — Figma uses non-token
              // `#222429` for the dark surface.
              className={`
                flex-1 min-h-10 min-w-0 px-[10px] flex flex-row overflow-hidden
                ${hasNewLine ? "rounded-md items-end" : "rounded-pill items-center"}
                ${composerInputClassName}
              `}
              style={{
                backgroundColor:
                  colorScheme === "dark"
                    ? composerDarkBackgroundColor
                      ? composerDarkBackgroundColor
                      : "#222429"
                    : composerLightBackgroundColor
                      ? composerLightBackgroundColor
                      : theme.colors.surface.secondary,
              }}
            >
              <View
                ref={setEditorArea}
                className="flex-1 min-w-0"
                // Web: cap growth + scroll internally past ~9 lines, with
                // even vertical padding. Native keeps its own sizing.
                style={isWeb ? WEB_EDITOR_SCROLL_STYLE : undefined}
              >
                {isWeb ? (
                  // Web path — true WYSIWYG via the contenteditable-
                  // backed `EnrichedRichTextEditor`. Mention picker
                  // + longText state machine are not yet ported here;
                  // they'll return in a follow-up. The host-side
                  // markdown state stays in sync via
                  // `onChangeMarkdown` so send-on-Enter still works.
                  <EnrichedRichTextEditor
                    ref={webEditorRef}
                    text={text}
                    // The contenteditable inherits its text color (CSS
                    // `color: inherit`), so set the themed primary color here —
                    // otherwise typed text is invisible in light mode.
                    textStyle={{ color: theme.colors.text.primary }}
                    placeholder={placeholder ?? RICH_TEXT_PLACEHOLDER}
                    onChangeText={(next) => {
                      setText(next);
                      setCaret(next.length);
                      setSelStart(next.length);
                      setSelEnd(next.length);
                      onComposerTextChange?.(next);
                    }}
                    onFocus={() => setIsInputFocused(true)}
                    onBlur={() => setIsInputFocused(false)}
                    onSubmit={() => {
                      void handleSend();
                    }}
                    pressEnterToSend={pressEnterToSend}
                    onActiveFormatsChange={setWebActive}
                    onImagePaste={handleEditorImagePaste}
                    onMentionTrigger={handleWebMentionTrigger}
                    onEmojiTrigger={handleWebEmojiTrigger}
                    onLinkEdit={handleLinkEdit}
                    onCodeLanguageOpen={handleCodeLanguageOpen}
                    {...(onEditLastMessage !== undefined
                      ? { onEditLast: onEditLastMessage }
                      : {})}
                  />
                ) : (
                  <RichTextEditor
                    ref={editorRef}
                    state={editorState}
                    text={text}
                    placeholder={placeholder ?? RICH_TEXT_PLACEHOLDER}
                    onChangeText={(next) => {
                      setText(next);
                      // RN doesn't always fire `onSelectionChange`
                      // before `onChangeText`; assume the caret sits
                      // at the end of the buffer so the mention
                      // parser sees fresh state.
                      setCaret(next.length);
                      setSelStart(next.length);
                      setSelEnd(next.length);
                      onComposerTextChange?.(next);
                    }}
                    onChangeState={setEditorState}
                    onFocus={() => setIsInputFocused(true)}
                    onBlur={() => setIsInputFocused(false)}
                    onSubmit={() => {
                      void handleSend();
                    }}
                    pressEnterToSend={pressEnterToSend}
                    onSelectionRangeChange={handleSelectionRangeChange}
                    onShortcut={handleShortcut}
                    {...(pendingSelection !== null
                      ? { selection: pendingSelection }
                      : {})}
                  />
                )}
              </View>
              <PopoverAnchor ref={emojiAnchorRef}>
                <Pressable
                  onPress={() => {
                    setShowEmojiPicker((open) => !open);
                    log.debug("composer.emoji.toggle", {});
                  }}
                  accessibilityRole="button"
                  accessibilityLabel="Insert emoji"
                  accessibilityState={{ expanded: showEmojiPicker }}
                  disabled={disabled}
                  // Emoji button — sits INSIDE the input pill, on
                  // the trailing edge. 26×26 so the surrounding pill
                  // still hugs the input height (40).
                  className={`w-[26px] h-[26px] rounded-pill ${hasNewLine ? "items-end mb-2" : "items-center"} justify-center ml-xs`}
                  testID="composer-emoji-button"
                >
                  <Smile
                    size={18}
                    strokeWidth={2}
                    color={theme.colors.text.secondary}
                  />
                </Pressable>
              </PopoverAnchor>
            </View>
            {/*
              Mic / Send swap. The composer shows ONE trailing
              affordance at a time:
                - Mic   → idle: input is unfocused AND empty AND the
                          host wired a voice handler.
                - Send  → active: the input has focus OR carries text.
              This matches Figma `2804:26711` and mirrors iOS/Android.
              Suppressed entirely in `editorOnly` mode (the composer is a
              plain rich-text FIELD — its owner provides its own submit).
            */}
            {editorOnly ? null : !isInputFocused &&
              !savedMenuOpen &&
              text.length === 0 &&
              !hasStagedAttachments &&
              showVoiceRecording &&
              onStartVoice !== undefined ? (
              <Pressable
                onPress={handleMicPress}
                accessibilityRole="button"
                accessibilityLabel="Record voice message"
                disabled={disabled}
                // Idle mic — 38×38 to match the Send slot it swaps with
                // (Design-system composer-desktop 3400:10718), so the
                // trailing affordance doesn't resize when text is typed.
                className={`
                  w-[38px] h-[38px] rounded-pill items-center justify-center
                  ${disabled ? "opacity-50" : ""}
                `}
                testID="composer-mic-button"
              >
                <Mic
                  size={22}
                  strokeWidth={2}
                  color={theme.colors.text.secondary}
                />
              </Pressable>
            ) : scheduleEnabled && canSend ? (
              // Unified indigo split-pill: [ Send | ⌄ ]. Both halves share
              // one rounded-pill indigo container divided by a hairline —
              // the send half fires immediately, the chevron half opens
              // the upward schedule popover. The container is the popover
              // anchor.
              <View
                ref={sendAreaRef}
                className="flex-row items-center h-[38px] rounded-pill overflow-hidden bg-light-accent-indigo dark:bg-dark-accent-indigo"
                testID="composer-send-split"
              >
                <Pressable
                  onPress={handleSend}
                  disabled={!canSend}
                  accessibilityRole="button"
                  accessibilityLabel="Send"
                  className="h-[38px] pl-2 pr-3 items-center justify-center active:opacity-90"
                  testID="composer-send-button"
                >
                  {isSending ? (
                    <Text className="font-sans text-base font-bold text-white">
                      …
                    </Text>
                  ) : (
                    <View style={{ transform: [{ rotateZ: "40deg" }] }}>
                      <Send size={20} strokeWidth={2} color="#FFFFFF" />
                    </View>
                  )}
                </Pressable>
                {/* Hairline divider between the two halves. */}
                <View className="w-px h-[22px] bg-white/25" />
                <Pressable
                  onPress={() => setScheduleMenuOpen((o) => !o)}
                  accessibilityRole="button"
                  accessibilityLabel="Schedule message"
                  accessibilityState={{ expanded: scheduleMenuOpen }}
                  className="h-[38px] pl-1 pr-2 items-center justify-center active:opacity-90"
                  testID="composer-schedule-chevron"
                >
                  <ChevronDown size={20} strokeWidth={2} color="#FFFFFF" />
                </Pressable>
              </View>
            ) : canSend || isSending ? (
              // Something to send (or a send in flight): the plain indigo
              // send pill. `isSending` keeps it mounted so the "…" indicator
              // shows through the async send.
              <Pressable
                onPress={handleSend}
                disabled={!canSend}
                accessibilityRole="button"
                accessibilityLabel="Send"
                // Figma `2804:26711` — 38×38 indigo pill.
                className={`
                  w-[38px] h-[38px] rounded-pill items-center justify-center
                  bg-light-accent-indigo dark:bg-dark-accent-indigo
                  ${!canSend ? "opacity-50" : ""}
                `}
                testID="composer-send-button"
              >
                {isSending ? (
                  <Text className="font-sans text-base font-bold text-white">
                    …
                  </Text>
                ) : (
                  <View
                    className="-ml-1"
                    style={{ transform: [{ rotateZ: "40deg" }] }}
                  >
                    <Send size={20} strokeWidth={2} color="#FFFFFF" />
                  </View>
                )}
              </Pressable>
            ) : savedEnabled ? (
              // Empty composer → Saved Messages bookmark (Figma
              // `4219:102584`). Replaces the disabled send button: an
              // enabled 38×38 pill that opens the client-side quick-reply
              // popover. The wrapper View is the popover anchor.
              <View ref={savedAreaRef} testID="composer-saved-area">
                <Pressable
                  onPress={() => setSavedMenuOpen((o) => !o)}
                  accessibilityRole="button"
                  accessibilityLabel="Saved messages"
                  accessibilityState={{ expanded: savedMenuOpen }}
                  // Keep editor focus on click (web) so the button doesn't
                  // swap to the idle Mic and unmount before the press fires.
                  {...webFocusGuard}
                  className="w-[38px] h-[38px] rounded-pill items-center justify-center bg-light-accent-indigo dark:bg-dark-accent-indigo active:opacity-90"
                  testID="composer-saved-button"
                >
                  <Bookmark size={20} strokeWidth={2} color="#FFFFFF" />
                </Pressable>
              </View>
            ) : (
              <Pressable
                onPress={handleSend}
                disabled={!canSend}
                accessibilityRole="button"
                accessibilityLabel="Send"
                // Figma `2804:26711` — 38×38 indigo pill.
                className={`
                  w-[38px] h-[38px] rounded-pill items-center justify-center
                  bg-light-accent-indigo dark:bg-dark-accent-indigo
                  ${!canSend ? "opacity-50" : ""}
                `}
                testID="composer-send-button"
              >
                <View
                  className="-ml-1"
                  style={{ transform: [{ rotateZ: "40deg" }] }}
                >
                  <Send size={20} strokeWidth={2} color="#FFFFFF" />
                </View>
              </Pressable>
            )}
          </>
        )}
      </View>
      {/*
        Schedule popover — opens UPWARD from the send button via the
        shared AnchoredPopover (placement="top" prefers above; the
        composer sits at the viewport bottom so it always has room).
        Quick presets schedule immediately; "Custom time…" opens the
        full modal.
      */}
      {scheduleEnabled ? (
        <AnchoredPopover
          anchorRef={sendAreaRef}
          open={scheduleMenuOpen}
          onClose={() => setScheduleMenuOpen(false)}
          placement="top"
          align="end"
          gap={8}
        >
          <ScheduleMenu
            onPick={(d) => void handleSchedule(d)}
            onCustom={() => {
              setScheduleMenuOpen(false);
              setScheduleModalOpen(true);
            }}
          />
        </AnchoredPopover>
      ) : null}
      {scheduleEnabled ? (
        <ScheduleMessageModal
          visible={scheduleModalOpen}
          onClose={() => setScheduleModalOpen(false)}
          onConfirm={(d) => void handleSchedule(d)}
        />
      ) : null}
      {/*
        Saved-messages popover — opens UPWARD from the bookmark button via
        the shared AnchoredPopover, mirroring the schedule menu. Picking a
        row fills the composer; the "+" header row opens the create modal.
      */}
      {savedEnabled ? (
        <AnchoredPopover
          anchorRef={savedAreaRef}
          open={savedMenuOpen}
          onClose={() => setSavedMenuOpen(false)}
          placement="top"
          align="end"
          gap={8}
        >
          <SavedMessagesMenu
            messages={savedList}
            onPick={fillComposerFromSaved}
            onDelete={handleRemoveSaved}
            onCreate={() => {
              setSavedMenuOpen(false);
              setSavedModalOpen(true);
            }}
          />
        </AnchoredPopover>
      ) : null}
      {savedEnabled ? (
        <CreateSavedMessageModal
          visible={savedModalOpen}
          onClose={() => setSavedModalOpen(false)}
          onSave={handleSaveNewMessage}
        />
      ) : null}
    </View>
  );
};

interface ScheduleMenuProps {
  readonly onPick: (sendAt: Date) => void;
  readonly onCustom: () => void;
}

/**
 * Schedule popover panel — quick presets + "Custom time…". Rendered
 * inside the shared AnchoredPopover (opens upward from the send button).
 */
const ScheduleMenu: React.FC<ScheduleMenuProps> = ({ onPick, onCustom }) => {
  const scheme = useColorScheme();
  const colorScheme: "light" | "dark" = scheme === "dark" ? "dark" : "light";
  const theme = createTheme(colorScheme);
  return (
    <View
      accessibilityRole="menu"
      accessibilityLabel="Schedule message"
      className="min-w-[248px] rounded-[12px] p-1 gap-0.5 bg-light-surface-primary dark:bg-[#15171C] border border-light-border-secondary dark:border-white/[0.08]"
      testID="composer-schedule-menu"
    >
      <Text className="px-2 pt-2 pb-1 font-sans text-[12px] font-semibold uppercase tracking-wide text-light-text-secondary dark:text-[#8B8F97]">
        Schedule
      </Text>
      {SCHEDULE_PRESETS.map((preset) => {
        const at = preset.compute();
        at.setSeconds(0, 0);
        return (
          <Pressable
            key={preset.id}
            accessibilityRole="menuitem"
            onPress={() => onPick(preset.compute())}
            className="flex flex-row items-center justify-between gap-3 px-2 py-2 rounded-lg hover:bg-light-surface-secondary dark:hover:bg-white/[0.08]"
            testID={`composer-schedule-preset-${preset.id}`}
          >
            <Text className="font-sans text-[14px] text-light-text-primary dark:text-[#F5F6F8]">
              {preset.label}
            </Text>
            <Text className="font-sans text-[12px] text-light-text-secondary dark:text-[#8B8F97]">
              {formatScheduleShort(at)}
            </Text>
          </Pressable>
        );
      })}
      <View className="h-px my-1 bg-light-border-secondary dark:bg-white/[0.08]" />
      <Pressable
        accessibilityRole="menuitem"
        onPress={onCustom}
        className="flex flex-row items-center gap-3 px-2 py-2 rounded-lg hover:bg-light-surface-secondary dark:hover:bg-white/[0.08]"
        testID="composer-schedule-custom"
      >
        <CalendarClock
          size={16}
          strokeWidth={2}
          color={theme.colors.text.secondary}
        />
        <Text className="font-sans text-[14px] text-light-text-primary dark:text-[#F5F6F8]">
          Custom time…
        </Text>
      </Pressable>
    </View>
  );
};

interface SavedMessagesMenuProps {
  readonly messages: readonly SavedMessage[];
  readonly onPick: (msg: SavedMessage) => void;
  readonly onDelete: (id: string) => void;
  readonly onCreate: () => void;
}

/**
 * Saved-messages popover panel — Figma `4219:102584`. A header ("SAVED
 * MESSAGES" + create button) over a scrollable list of quick replies; each
 * row fills the composer on press, with a delete affordance and an insert
 * arrow. Rendered inside the shared AnchoredPopover (opens upward from the
 * bookmark button). Presentational — the composer owns the store + state.
 */
const SavedMessagesMenu: React.FC<SavedMessagesMenuProps> = ({
  messages,
  onPick,
  onDelete,
  onCreate,
}) => {
  const scheme = useColorScheme();
  const colorScheme: "light" | "dark" = scheme === "dark" ? "dark" : "light";
  const theme = createTheme(colorScheme);
  // Insert-arrow tint — the composer's usual indigo accent (`#537EFF`), not
  // the Figma mock's one-off blue.
  const accentBlue = theme.colors.accent.indigo;
  return (
    <View
      accessibilityRole="menu"
      accessibilityLabel="Saved messages"
      // Figma panel: 300 wide, radius 12, padding 8, fill `#1C1F26`.
      className="w-[300px] rounded-[12px] p-2 bg-light-surface-primary dark:bg-[#1C1F26] border border-light-border-secondary dark:border-white/[0.08]"
      testID="composer-saved-menu"
    >
      {/* Header — title + create ("+"). */}
      <View className="flex-row items-center justify-between px-2 pt-1 pb-2">
        <Text className="font-sans text-[12px] font-semibold uppercase tracking-wide text-light-text-secondary dark:text-[#6B7280]">
          Saved Messages
        </Text>
        <Pressable
          onPress={onCreate}
          accessibilityRole="button"
          accessibilityLabel="New saved message"
          className="w-6 h-6 items-center justify-center rounded-md active:opacity-60 hover:bg-light-surface-secondary dark:hover:bg-white/[0.08]"
          testID="composer-saved-create"
        >
          <Plus size={16} strokeWidth={2} color={theme.colors.text.secondary} />
        </Pressable>
      </View>

      {messages.length === 0 ? (
        // Empty state — explain what will appear (ui-design empty-state rule).
        <View className="px-3 py-6 items-center gap-1">
          <Text className="font-sans text-[13px] text-light-text-primary dark:text-[#F5F6F8]">
            No saved messages yet
          </Text>
          <Text className="font-sans text-[12px] text-center text-light-text-secondary dark:text-[#6B7280]">
            Tap ＋ to save a quick reply you can drop into any chat.
          </Text>
        </View>
      ) : (
        <ScrollView
          // Cap the list so a long set scrolls internally instead of growing
          // the popover past the viewport. Row padding provides the inter-row
          // separation (a `gap` on ScrollView would style the scroller, not
          // the content).
          style={{ maxHeight: 320 }}
          showsVerticalScrollIndicator={true}
          keyboardShouldPersistTaps="handled"
        >
          {messages.map((msg) => (
            <Pressable
              key={msg.id}
              accessibilityRole="menuitem"
              accessibilityLabel={
                msg.title !== undefined && msg.title.length > 0
                  ? `${msg.title}: ${msg.text}`
                  : msg.text
              }
              onPress={() => onPick(msg)}
              className="flex-row items-center gap-2 px-3 py-3 rounded-[8px] hover:bg-light-surface-secondary dark:hover:bg-white/[0.06]"
              testID={`composer-saved-row-${msg.id}`}
            >
              <Text
                numberOfLines={1}
                className="flex-1 min-w-0 font-sans text-[13px] text-light-text-primary dark:text-[#F5F6F8]"
              >
                {msg?.title || msg?.text}
              </Text>
              <Pressable
                onPress={(e) => {
                  // Don't let the delete tap also fill the composer.
                  (e as { stopPropagation?: () => void }).stopPropagation?.();
                  onDelete(msg.id);
                }}
                accessibilityRole="button"
                accessibilityLabel="Delete saved message"
                className="w-6 h-6 items-center justify-center rounded-md opacity-60 active:opacity-100 hover:bg-light-surface-secondary dark:hover:bg-white/[0.08]"
                testID={`composer-saved-delete-${msg.id}`}
              >
                <Trash2
                  size={13}
                  strokeWidth={2}
                  color={theme.colors.text.secondary}
                />
              </Pressable>
              <ArrowRight size={14} strokeWidth={2} color={accentBlue} />
            </Pressable>
          ))}
        </ScrollView>
      )}
    </View>
  );
};

interface AttachmentMenuProps {
  readonly onSelect: (kind: ChatRoomAttachMenuKind) => void;
  /** When set, only these entry kinds render (in the default order). */
  readonly kinds?: ReadonlyArray<ChatRoomAttachMenuKind> | undefined;
}

// `lucide-react-native` icon per attach-menu kind. Mirrors Figma
// `2804:26754` (Action menu) — the icons are the lucide equivalents
// of the design's stroke set, sized to 20 to match the 24-tall row
// (leading 20px icon + 12px gap + label).
const ATTACH_MENU_ICONS: Record<ChatRoomAttachMenuKind, LucideIcon> = {
  camera: Camera,
  photo: ImageIcon,
  doc: FileText,
  location: MapPin,
  poll: BarChart3,
  event: CalendarPlus,
  games: Dices,
  voice: Mic,
};

// Visible entries + ordering per Figma `2804:26754`. Voice is
// intentionally omitted from the menu — the mic affordance lives
// inside the input pill in the redesigned composer.
const ATTACH_MENU_ENTRIES: ReadonlyArray<{
  kind: ChatRoomAttachMenuKind;
  label: string;
}> = [
  { kind: "photo", label: "Add images" },
  { kind: "doc", label: "Add files" },
  { kind: "camera", label: "Camera" },
  { kind: "location", label: "Location" },
  { kind: "poll", label: "Poll" },
  { kind: "event", label: "Event" },
  { kind: "games", label: "Games" },
];

const AttachmentMenu: React.FC<AttachmentMenuProps> = ({ onSelect, kinds }) => {
  const scheme = useColorScheme();
  const colorScheme = scheme === "dark" ? "dark" : "light";
  const theme = createTheme(colorScheme);

  const iconColor =
    colorScheme === "dark" ? "#B5BAC3" : theme.colors.text.secondary;

  const entries =
    kinds !== undefined
      ? ATTACH_MENU_ENTRIES.filter((e) => kinds.includes(e.kind))
      : ATTACH_MENU_ENTRIES;

  return (
    <View
      accessibilityRole="menu"
      accessibilityLabel="Attachment options"
      // Design-system composer-plus-menu (4058:6422): compact flat
      // card, ~200 wide, radius 12, padding 4, gap 4 — no border.
      className="min-w-[200px] rounded-[12px] p-1 gap-1 bg-light-surface-primary dark:bg-[#15171C]"
      testID="composer-attach-menu"
    >
      {entries.map((entry) => {
        const Icon = ATTACH_MENU_ICONS[entry.kind];
        return (
          <Pressable
            key={entry.kind}
            onPress={() => onSelect(entry.kind)}
            accessibilityRole="menuitem"
            accessibilityLabel={entry.label}
            testID={`composer-attach-menu-${entry.kind}`}
            // 36-tall row, padding 8, gap 12, radius 8; hover tint via
            // NativeWind hover: variants (web) — no inline style needed.
            className="flex flex-row items-center gap-3 px-2 py-2 rounded-lg hover:bg-light-surface-secondary dark:hover:bg-white/[0.08]"
          >
            <Icon size={20} strokeWidth={2} color={iconColor} />
            <Text className="font-sans text-[14px] leading-5 font-normal text-light-text-primary dark:text-[#F5F6F8]">
              {entry.label}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
};
